import React, { useState, useRef, useEffect } from 'react';
import { flushSync } from 'react-dom';
import NewNote from "./NewNote";
import GraphViewVis from "./GraphViewVis";
import { IconButton, useMediaQuery, useTheme } from '@mui/material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import SettingsIcon from '@mui/icons-material/Settings';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import MenuIcon from '@mui/icons-material/Menu';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import PlaceIcon from '@mui/icons-material/Place';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import { SparklesIcon } from '@heroicons/react/24/solid';
import { useLocation, useNavigate } from 'react-router-dom';
import { USE_MOCK_DATA, mockNotes, mockGraphData } from '../utils/mockData';
import { apiFetch } from './apiFetch';
import { API_ENDPOINTS } from '../config/apiConfig';

const NOTES_URL = API_ENDPOINTS.notes;
const NOTE_URL = API_ENDPOINTS.note;
const NOTE_SUMMARIES_URL = API_ENDPOINTS.noteSummaries;
const GRAPH_URL = API_ENDPOINTS.graphData;
const NOTE_GRAPH_URL = API_ENDPOINTS.graphDataByNote;

const NoteView = ({ selectedNote, setSelectedNote, onNewNote }) => {
  const [refreshNotes, setRefreshNotes] = useState(false); // Add refresh state
  const [refreshSubgraph, setRefreshSubgraph] = useState(false); // Add subgraph refresh state
  const [mobileEditorOpen, setMobileEditorOpen] = useState(false);
  const [mobileSearchValue, setMobileSearchValue] = useState("");
  const [mobileSearchResults, setMobileSearchResults] = useState({ concepts: [], notes: [] });
  const [mobileNotes, setMobileNotes] = useState([]);
  const [mobileNoteSummaries, setMobileNoteSummaries] = useState({});
  const [deletingNoteId, setDeletingNoteId] = useState(null);
  const [noteRowMenuOpenId, setNoteRowMenuOpenId] = useState(null);
  const [mobilePrimaryView, setMobilePrimaryView] = useState("home"); // home | notes
  const [desktopGraphView, setDesktopGraphView] = useState(null); // { centerConcept, originConcept } | null
  const [notesSearchValue, setNotesSearchValue] = useState("");
  const [notesSortOrder, setNotesSortOrder] = useState("newest"); // newest | oldest
  const [notesSortMenuOpen, setNotesSortMenuOpen] = useState(false);
  const [homeConceptTypeFilter, setHomeConceptTypeFilter] = useState("All");
  const [homeMoreMenuOpen, setHomeMoreMenuOpen] = useState(false);
  const [graphData, setGraphData] = useState({ nodes: [], edges: [] });
  const [selectedConcept, setSelectedConcept] = useState(null);
  const [searchPage, setSearchPage] = useState("home");
  const [searchQueryCommitted, setSearchQueryCommitted] = useState("");
  const [linkedConcepts, setLinkedConcepts] = useState([]);
  const [showLinkedConcepts, setShowLinkedConcepts] = useState(true);
  const [highlightQuery, setHighlightQuery] = useState("");
  const [keyboardVisible, setKeyboardVisible] = useState(false);
  const [editorMenuOpen, setEditorMenuOpen] = useState(false);
  const [mobileHeaderMenuOpen, setMobileHeaderMenuOpen] = useState(false);
  const editorRef = useRef(null);
  const editorMenuRef = useRef(null);
  const searchResultsRef = useRef({ concepts: [], notes: [] });
  const location = useLocation();
  const navigate = useNavigate();
  const handleLogout = React.useCallback(() => {
    localStorage.removeItem('authToken');
    navigate('/signin');
  }, [navigate]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isMobileEditorRoute = isMobile && location.pathname === '/newnote';
  const searchInputValue = mobileSearchValue.trim();
  const activeSearchQuery = searchPage === "results" ? searchQueryCommitted : searchInputValue;
  const recognizedConcepts = React.useMemo(
    () => mobileSearchResults.concepts.filter(concept => concept.score >= 0.6),
    [mobileSearchResults.concepts]
  );
  const canEnterGraphFromSearch = recognizedConcepts.length >= 2;
  const lastPrefillRef = useRef("");

  // Mobile view state: 'list' or 'editor'
  // Derived from selectedNote: if selectedNote is null (or 'new'), we might be in editor mode if intent was clear
  // On mobile, open editor only when user explicitly enters edit mode.
  const showEditorOnMobile = mobileEditorOpen;
  const showRefineFab = isMobile ? showEditorOnMobile : true;
  const [canRefine, setCanRefine] = useState(false);
  const [showRefineHint, setShowRefineHint] = useState(false);
  const [refineUiState, setRefineUiState] = useState('idle'); // idle | processing | results | error
  const [refineInsights, setRefineInsights] = useState({ entities: [], relations: [] });
  const [refineInsightsNoteId, setRefineInsightsNoteId] = useState(null);
  const [refineError, setRefineError] = useState('');
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [showingRefinedStaticView, setShowingRefinedStaticView] = useState(false);
  const showDesktopGraphInPane = !isMobile && Boolean(desktopGraphView?.centerConcept);
  const showFloatingEditorControls = showRefineFab && !showingRefinedStaticView && !showDesktopGraphInPane;
  const isRefining = refineUiState === 'processing';
  const showRefinePanel = refineUiState !== 'idle';
  const refineHintTimeoutRef = useRef(null);
  const applyRefineState = React.useCallback((note, isRefined, overrides = {}) => {
    if (!note) return note;
    return {
      ...note,
      ...overrides,
      is_refined: Boolean(isRefined),
      refined: Boolean(isRefined),
      refine_state: isRefined ? 'refined' : 'draft'
    };
  }, []);
  const inferIsRefined = React.useCallback((note, summaryMap) => {
    if (!note) return false;
    if (note.refined === true || note.is_refined === true || note.refine_state === 'refined') {
      return true;
    }
    const summary = summaryMap[note.id] || summaryMap[String(note.id)] || note.note_summary || note.summary || '';
    return typeof summary === 'string' && summary.trim().length > 0;
  }, []);
  const normalizeRefineInsights = React.useCallback((graphResponse) => {
    const graphItems = Array.isArray(graphResponse) ? graphResponse : [graphResponse];
    const entities = [];
    const relations = [];
    const entitySeen = new Set();
    const relationSeen = new Set();

    graphItems.forEach((graph) => {
      (graph?.nodes || []).forEach((node, index) => {
        const name = (node.entity_name || node.label || node.id || '').trim();
        if (!name) return;
        const type = node.entity_type || node.entityType || 'Concept';
        const key = `${name.toLowerCase()}::${type.toLowerCase()}`;
        if (entitySeen.has(key)) return;
        entitySeen.add(key);
        entities.push({
          id: `${key}-${index}`,
          name,
          type
        });
      });

      (graph?.edges || []).forEach((edge, index) => {
        const source = (edge.source || edge.from || '').trim();
        const target = (edge.target || edge.to || '').trim();
        if (!source || !target) return;
        const label = (edge.label || edge.relationship || edge.relation || edge.type || 'related to').trim();
        const key = `${source.toLowerCase()}::${label.toLowerCase()}::${target.toLowerCase()}`;
        if (relationSeen.has(key)) return;
        relationSeen.add(key);
        relations.push({
          id: `${key}-${index}`,
          source,
          label,
          target
        });
      });
    });

    return {
      entities: entities.slice(0, 24),
      relations: relations.slice(0, 24)
    };
  }, []);
  const fetchRefineInsights = React.useCallback(async (noteId) => {
    if (USE_MOCK_DATA) {
      return normalizeRefineInsights(mockGraphData);
    }
    if (!noteId) {
      return { entities: [], relations: [] };
    }
    const token = localStorage.getItem('authToken');
    if (!token) {
      return { entities: [], relations: [] };
    }
    const response = await fetch(`${NOTE_GRAPH_URL}?note_id=${encodeURIComponent(noteId)}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch refined note graph data');
    }
    const data = await response.json();
    return normalizeRefineInsights(data);
  }, [normalizeRefineInsights]);
  const fetchRefineStatus = React.useCallback(async (noteId) => {
    if (!noteId) return null;
    const token = localStorage.getItem('authToken');
    if (!token) return null;
    const response = await fetch(`${NOTE_URL}/${encodeURIComponent(noteId)}/refine-status`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) {
      throw new Error('Failed to fetch refine status');
    }
    return response.json();
  }, []);
  const mergeRefineInsightsIntoGraph = React.useCallback((currentGraph, insights) => {
    const baseNodes = Array.isArray(currentGraph?.nodes) ? currentGraph.nodes : [];
    const baseEdges = Array.isArray(currentGraph?.edges) ? currentGraph.edges : [];
    const nodeKeySet = new Set(
      baseNodes.map((node) => (node.entity_name || node.label || node.id || '').toLowerCase()).filter(Boolean)
    );
    const edgeKeySet = new Set(
      baseEdges.map((edge) => {
        const source = (edge.source || edge.from || '').toLowerCase();
        const target = (edge.target || edge.to || '').toLowerCase();
        const label = (edge.label || edge.relationship || edge.relation || edge.type || '').toLowerCase();
        return `${source}::${label}::${target}`;
      }).filter(Boolean)
    );

    const nextNodes = [...baseNodes];
    const nextEdges = [...baseEdges];

    (insights?.entities || []).forEach((entity) => {
      const name = (entity?.name || '').trim();
      if (!name) return;
      const key = name.toLowerCase();
      if (nodeKeySet.has(key)) return;
      nodeKeySet.add(key);
      nextNodes.push({
        id: name,
        entity_name: name,
        label: name,
        entity_type: entity.type || 'Concept'
      });
    });

    (insights?.relations || []).forEach((relation, index) => {
      const source = (relation?.source || '').trim();
      const target = (relation?.target || '').trim();
      if (!source || !target) return;
      const label = (relation?.label || 'related to').trim();
      const key = `${source.toLowerCase()}::${label.toLowerCase()}::${target.toLowerCase()}`;
      if (edgeKeySet.has(key)) return;
      edgeKeySet.add(key);
      nextEdges.push({
        id: relation.id || `refined-${source}-${target}-${index}`,
        source,
        target,
        label
      });
    });

    return { nodes: nextNodes, edges: nextEdges };
  }, []);
  const handleRefine = async () => {
    if (isRefining) return;
    if (!canRefine) {
      if (refineHintTimeoutRef.current) {
        clearTimeout(refineHintTimeoutRef.current);
      }
      setShowRefineHint(true);
      refineHintTimeoutRef.current = setTimeout(() => {
        setShowRefineHint(false);
      }, 2200);
      return;
    }
    setShowRefineHint(false);
    setRefineError('');
    setRefineInsights({ entities: [], relations: [] });
    setRefineInsightsNoteId(null);
    setRefineUiState('processing');
    const result = await editorRef.current?.refineNote?.();
    if (!result?.success) {
      setRefineUiState('error');
      setRefineError('Unable to refine right now. Please try again.');
      return;
    }
    try {
      const refinedNoteId = result.noteId || selectedNote?.id;
      if (setSelectedNote) {
        setSelectedNote((previous) => {
          const current = previous || selectedNote || { id: refinedNoteId || 'new', title: '', content: '' };
          const withResolvedId = refinedNoteId ? { ...current, id: refinedNoteId } : current;
          return applyRefineState(withResolvedId, false, {
            refine_status: result.refine_status || 'queued',
            content_hash: result.content_hash || withResolvedId.content_hash,
            queued_content_hash: result.content_hash || withResolvedId.queued_content_hash || null,
            refine_error: result.refine_error || '',
          });
        });
      }
      let latestStatus = null;
      for (let attempt = 0; attempt < 30; attempt += 1) {
        latestStatus = await fetchRefineStatus(refinedNoteId);
        if (latestStatus?.refine_status === 'completed') break;
        if (latestStatus?.refine_status === 'failed') {
          throw new Error(latestStatus?.refine_error || 'Refine failed');
        }
        await new Promise((resolve) => window.setTimeout(resolve, 2000));
      }
      if (!latestStatus || latestStatus?.refine_status !== 'completed') {
        throw new Error('Refine is still processing. Please check again shortly.');
      }
      if (setSelectedNote) {
        setSelectedNote((previous) => applyRefineState(previous || selectedNote, true, latestStatus));
      }
      const nextInsights = await fetchRefineInsights(refinedNoteId);
      setRefineInsights(nextInsights);
      setRefineInsightsNoteId(refinedNoteId || null);
      setGraphData((currentGraph) => mergeRefineInsightsIntoGraph(currentGraph, nextInsights));
      setRefineUiState('results');
      setRefreshSubgraph(r => !r);
    } catch (error) {
      setRefineUiState('error');
      setRefineError(error?.message || 'Refined note saved, but we could not load entities and relations.');
    }
  };
  const handleSaveDraft = async () => {
    if (isSavingDraft) return;
    setShowRefineHint(false);
    setIsSavingDraft(true);
    try {
      await editorRef.current?.saveDraft?.();
    } finally {
      setIsSavingDraft(false);
    }
  };
  const handleRefineState = React.useCallback((enabled) => {
    setCanRefine(enabled);
  }, []);

  useEffect(() => {
    const isSelectedRefined = Boolean(
      selectedNote?.refined === true
      || selectedNote?.is_refined === true
      || selectedNote?.refine_state === 'refined'
    );
    if (isSelectedRefined) {
      return;
    }
    setRefineUiState('idle');
    setRefineInsights({ entities: [], relations: [] });
    setRefineInsightsNoteId(null);
    setRefineError('');
  }, [selectedNote?.id, selectedNote?.refined, selectedNote?.is_refined, selectedNote?.refine_state]);

  useEffect(() => {
    return () => {
      if (refineHintTimeoutRef.current) {
        clearTimeout(refineHintTimeoutRef.current);
      }
    };
  }, [refreshSubgraph]);

  // Handler to toggle refreshNotes
  const handleNotesChanged = () => {
    setRefreshNotes(r => !r);
    setRefreshSubgraph(r => !r); // Also refresh subgraph
  };

  useEffect(() => {
    const incomingQuery = location.state?.searchQuery;
    if (typeof incomingQuery !== "string") return;
    if (incomingQuery === lastPrefillRef.current) return;
    lastPrefillRef.current = incomingQuery;
    setMobileSearchValue(incomingQuery);
    setSearchQueryCommitted(incomingQuery);
    setSearchPage("results");
  }, [location.state]);

  useEffect(() => {
    const viewParam = new URLSearchParams(location.search).get("view");
    const openView = viewParam || location.state?.openView;
    if (!isMobile || location.pathname !== "/app") return;
    if (openView === "notes") {
      setMobilePrimaryView("notes");
      setSelectedConcept(null);
      setSearchPage("home");
      setSearchQueryCommitted("");
      setMobileSearchValue("");
      setHighlightQuery("");
      setLinkedConcepts([]);
      setMobileEditorOpen(false);
      if (setSelectedNote) {
        setSelectedNote(null);
      }
      return;
    }
    if (openView === "home") {
      setMobilePrimaryView("home");
      setSelectedConcept(null);
      setSearchPage("home");
      setSearchQueryCommitted("");
      setMobileSearchValue("");
    }
  }, [isMobile, location.pathname, location.search, location.state?.openView, setSelectedNote]);

  const normalizeText = (value) => value.toLowerCase().trim();
  const scoreTextMatch = React.useCallback((query, text) => {
    if (!query || !text) return 0;
    const q = normalizeText(query);
    const t = normalizeText(text);
    if (!q) return 0;
    if (t === q) return 1;
    if (t.startsWith(q)) return 0.9;
    if (t.includes(q)) return 0.7;
    let qi = 0;
    for (let ti = 0; ti < t.length && qi < q.length; ti += 1) {
      if (t[ti] === q[qi]) qi += 1;
    }
    if (qi === q.length) return 0.45;
    return 0;
  }, []);
  const extractNoteText = (note) => {
    if (!note?.content) return "";
    try {
      const parsed = JSON.parse(note.content);
      return (parsed.blocks || []).map(block => block.text).join("\n");
    } catch (error) {
      return "";
    }
  };

  const conceptNodes = React.useMemo(() => {
    if (!graphData?.nodes) return [];
    return graphData.nodes.map((node, index) => ({
      id: node.entity_name || node.id || `${node.label}-${index}`,
      name: node.entity_name || node.label || node.id || "Untitled",
      type: node.entity_type || node.entityType || "Topic"
    }));
  }, [graphData]);
  const lastConceptPrefillRef = useRef("");

  useEffect(() => {
    const incomingConcept = location.state?.conceptName;
    if (typeof incomingConcept !== "string" || !incomingConcept) return;
    if (incomingConcept === lastConceptPrefillRef.current) return;
    if (!conceptNodes.length) return;
    const match = conceptNodes.find(concept => (
      concept.name === incomingConcept || concept.id === incomingConcept
    ));
    if (!match) return;
    lastConceptPrefillRef.current = incomingConcept;
    setSelectedConcept(match);
    setSearchPage("concept");
  }, [location.state?.conceptName, conceptNodes]);

  const conceptConnections = React.useMemo(() => {
    if (!conceptNodes.length || !mobileNotes.length) return {};
    const counts = {};
    const noteTexts = mobileNotes.map(note => ({
      id: note.id,
      text: `${note.title || ""}\n${extractNoteText(note)}`.toLowerCase()
    }));
    conceptNodes.forEach(concept => {
      const conceptName = concept.name.toLowerCase();
      counts[concept.name] = noteTexts.reduce((total, note) => (
        note.text.includes(conceptName) ? total + 1 : total
      ), 0);
    });
    return counts;
  }, [conceptNodes, mobileNotes]);
  const matchesHomeFilter = React.useCallback((type, filter) => {
    const normalized = (type || "").toLowerCase();
    if (filter === "People") {
      return normalized.includes("person") || normalized.includes("people");
    }
    if (filter === "Event" || filter === "Events") {
      return normalized.includes("event") || normalized.includes("meeting");
    }
    if (filter === "Project" || filter === "Projects") {
      return normalized.includes("project");
    }
    if (filter === "All") {
      return true;
    }
    return normalized === String(filter || "").toLowerCase();
  }, []);
  const conceptTypeOptions = React.useMemo(() => {
    return Array.from(new Set(conceptNodes.map(concept => concept.type).filter(Boolean))).sort((a, b) => a.localeCompare(b));
  }, [conceptNodes]);
  const otherConceptTypeOptions = React.useMemo(() => {
    return conceptTypeOptions.filter((type) => (
      !matchesHomeFilter(type, "People")
      && !matchesHomeFilter(type, "Events")
      && !matchesHomeFilter(type, "Projects")
    ));
  }, [conceptTypeOptions, matchesHomeFilter]);
  const mobileExploreTags = React.useMemo(() => {
    const conceptsWithCounts = conceptNodes
      .filter(concept => matchesHomeFilter(concept.type, homeConceptTypeFilter))
      .map(concept => ({
        name: concept.name,
        mentions: conceptConnections[concept.name] || 0
      }))
      .sort((a, b) => {
        if (b.mentions === a.mentions) {
          return a.name.localeCompare(b.name);
        }
        return b.mentions - a.mentions;
      })
      .slice(0, 30)
      .map(item => item.name);

    return conceptsWithCounts;
  }, [conceptNodes, conceptConnections, homeConceptTypeFilter, matchesHomeFilter]);

  const getConceptIcon = (type) => {
    const normalized = (type || "").toLowerCase();
    if (normalized.includes("person")) return <PersonOutlineIcon fontSize="small" />;
    if (normalized.includes("place")) return <PlaceIcon fontSize="small" />;
    if (normalized.includes("project") || normalized.includes("meeting")) return <WorkOutlineIcon fontSize="small" />;
    return <LocalOfferIcon fontSize="small" />;
  };

  const getNoteSnippet = (text, query) => {
    const cleanText = text.replace(/\s+/g, " ").trim();
    if (!cleanText) return "";
    if (!query) return cleanText.slice(0, 90);
    const lower = cleanText.toLowerCase();
    const q = query.toLowerCase();
    const matchIndex = lower.indexOf(q);
    if (matchIndex === -1) return cleanText.slice(0, 90);
    const start = Math.max(0, matchIndex - 34);
    const end = Math.min(cleanText.length, matchIndex + q.length + 34);
    const prefix = start > 0 ? "..." : "";
    const suffix = end < cleanText.length ? "..." : "";
    return `${prefix}${cleanText.slice(start, end)}${suffix}`;
  };
  const getAiSummaryForNote = React.useCallback((note) => {
    const noteId = note?.id;
    const mappedSummary = mobileNoteSummaries[noteId] || mobileNoteSummaries[String(noteId)];
    if (typeof mappedSummary === "string" && mappedSummary.trim()) {
      return mappedSummary.trim();
    }
    if (typeof note?.note_summary === "string" && note.note_summary.trim()) {
      return note.note_summary.trim();
    }
    if (typeof note?.summary === "string" && note.summary.trim()) {
      return note.summary.trim();
    }
    return "";
  }, [mobileNoteSummaries]);

  useEffect(() => {
    if (USE_MOCK_DATA) {
      setMobileNotes(mockNotes);
      setMobileNoteSummaries({});
      return;
    }
    const token = localStorage.getItem("authToken");
    if (!token) return;
    apiFetch(NOTES_URL, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      }
    })
      .then(async response => {
        if (!response.ok) throw new Error("Failed to fetch notes");
        return response.json();
      })
      .then(data => setMobileNotes(Array.isArray(data) ? data : []))
      .catch(() => {});

    apiFetch(NOTE_SUMMARIES_URL, {
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      }
    })
      .then(async response => {
        if (!response.ok) throw new Error("Failed to fetch note summaries");
        return response.json();
      })
      .then(data => {
        const summaryMap = {};
        (Array.isArray(data) ? data : []).forEach(item => {
          if (item?.note_id) {
            summaryMap[item.note_id] = item.note_summary || "";
            summaryMap[String(item.note_id)] = item.note_summary || "";
          }
        });
        setMobileNoteSummaries(summaryMap);
        setMobileNotes(prev => prev.map(note => applyRefineState(note, inferIsRefined(note, summaryMap))));
      })
      .catch(() => {
        setMobileNoteSummaries({});
      });
  }, [applyRefineState, inferIsRefined, refreshNotes]);

  useEffect(() => {
    if (USE_MOCK_DATA) {
      setGraphData(mockGraphData);
      return;
    }
    const token = localStorage.getItem("authToken");
    if (!token) return;
    fetch(GRAPH_URL, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          const nodes = [];
          const edges = [];
          const seen = new Set();
          data.forEach(graph => {
            (graph.nodes || []).forEach(node => {
              const key = node.entity_name || node.id || node.label;
              if (key && !seen.has(key)) {
                nodes.push(node);
                seen.add(key);
              }
            });
            (graph.edges || []).forEach(edge => edges.push(edge));
          });
          setGraphData({ nodes, edges });
          return;
        }
        setGraphData(data || { nodes: [], edges: [] });
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!activeSearchQuery) {
      setMobileSearchResults({ concepts: [], notes: [] });
      return;
    }
    const delay = activeSearchQuery.length <= 1 ? 0 : 150;
    const handle = setTimeout(() => {
      const conceptMatches = conceptNodes
        .map(concept => {
          const score = scoreTextMatch(activeSearchQuery, concept.name);
          return { ...concept, score };
        })
        .sort((a, b) => b.score - a.score);
      const noteMatches = mobileNotes
        .map(note => {
          const titleScore = scoreTextMatch(activeSearchQuery, note.title || "");
          const contentText = extractNoteText(note);
          const bodyScore = scoreTextMatch(activeSearchQuery, contentText);
          const score = Math.max(titleScore * 1.15, bodyScore);
          return {
            ...note,
            score,
            snippet: getNoteSnippet(`${note.title || ""} ${contentText}`, activeSearchQuery)
          };
        })
        .sort((a, b) => b.score - a.score);
      const sticky = (items, previous, maxSticky = 2) => {
        if (!previous.length) return items;
        const topScore = items[0]?.score || 0;
        const stickyIds = previous
          .slice(0, maxSticky)
          .filter(item => item.score >= topScore * 0.6)
          .map(item => item.id || item.name);
        const stickyItems = [];
        const rest = [];
        items.forEach(item => {
          const key = item.id || item.name;
          if (stickyIds.includes(key)) {
            stickyItems.push(item);
          } else {
            rest.push(item);
          }
        });
        return [...stickyItems, ...rest];
      };
      const previousResults = searchResultsRef.current || { concepts: [], notes: [] };
      const nextConcepts = sticky(conceptMatches, previousResults.concepts || []);
      const nextNotes = sticky(noteMatches, previousResults.notes || []);
      const nextResults = { concepts: nextConcepts, notes: nextNotes };
      searchResultsRef.current = nextResults;
      setMobileSearchResults(nextResults);
    }, delay);
    return () => clearTimeout(handle);
  }, [activeSearchQuery, conceptNodes, mobileNotes, scoreTextMatch]);

  const openDesktopFullGraph = React.useCallback((centerConcept) => {
    if (!centerConcept) return;
    setDesktopGraphView({
      centerConcept,
      originConcept: centerConcept,
    });
  }, []);

  const closeDesktopFullGraph = React.useCallback(() => {
    setDesktopGraphView(null);
  }, []);

  const handleDesktopGraphOpenSearch = React.useCallback((query) => {
    closeDesktopFullGraph();
    setMobilePrimaryView("home");
    setSelectedConcept(null);
    setMobileSearchValue(query || "");
    setSearchQueryCommitted(query || "");
    setSearchPage(query ? "results" : "home");
  }, [closeDesktopFullGraph]);

  // Handler to open the editor when adding a new note
  const handleNewNote = React.useCallback(() => {
    closeDesktopFullGraph();
    setHighlightQuery("");
    setLinkedConcepts([]);
    // Set a dummy selectedNote and open the editor in the same tick for iOS focus.
    flushSync(() => {
      setMobileEditorOpen(true);
      if (setSelectedNote) {
        setSelectedNote({ id: 'new', title: '', content: '', _forceOpen: true });
      }
    });
    if (isMobile && location.pathname !== '/newnote') {
      navigate('/newnote');
    }
    editorRef.current?.focusEditor?.();
    requestAnimationFrame(() => {
      editorRef.current?.focusEditor?.();
    });
    // Remove the call to onNewNote to prevent parent state reset/collapse
  }, [closeDesktopFullGraph, isMobile, location.pathname, navigate, setHighlightQuery, setLinkedConcepts, setSelectedNote]);

  const handleOpenHome = React.useCallback(() => {
    closeDesktopFullGraph();
    setMobilePrimaryView("home");
    setHomeConceptTypeFilter("All");
    setSelectedConcept(null);
    setSearchPage("home");
    setSearchQueryCommitted("");
    setMobileSearchValue("");
    if (isMobile && location.pathname !== '/app') {
      navigate('/app');
    }
  }, [closeDesktopFullGraph, isMobile, location.pathname, navigate]);
  const handleOpenNotesList = React.useCallback(() => {
    closeDesktopFullGraph();
    setMobilePrimaryView("notes");
    setNotesSortMenuOpen(false);
    setSelectedConcept(null);
    setSearchPage("home");
    setSearchQueryCommitted("");
    setMobileSearchValue("");
    setHighlightQuery("");
    setLinkedConcepts([]);
    setMobileEditorOpen(false);
    if (setSelectedNote) {
      setSelectedNote(null);
    }
    if (isMobile && location.pathname !== '/app') {
      navigate('/app');
    }
  }, [closeDesktopFullGraph, isMobile, location.pathname, navigate, setSelectedNote]);
  const handleOpenSupport = React.useCallback(() => {
    closeDesktopFullGraph();
    if (isMobile) {
      navigate('/profile');
      return;
    }
    setMobilePrimaryView("support");
    setNotesSortMenuOpen(false);
    setHomeMoreMenuOpen(false);
    setSearchPage("home");
  }, [closeDesktopFullGraph, isMobile, navigate]);

  const handleSelectNote = (note) => {
    closeDesktopFullGraph();
    const normalizedNote = applyRefineState(note, inferIsRefined(note, mobileNoteSummaries));
    setSelectedNote(normalizedNote);
    setShowingRefinedStaticView(false);
    setMobileEditorOpen(true);
    setHighlightQuery("");
    setLinkedConcepts([]);
    if (isMobile && location.pathname !== '/newnote') {
      navigate('/newnote');
    }
  };
  const deleteNoteById = React.useCallback(async (noteId, closeEditor = false) => {
    if (!noteId || noteId === 'new') return;
    if (deletingNoteId) return;
    const confirmed = window.confirm("Delete this note?");
    if (!confirmed) return;

    const token = localStorage.getItem("authToken");
    if (!token) return;

    try {
      setDeletingNoteId(noteId);
      const response = await fetch(`${NOTE_URL}/${encodeURIComponent(noteId)}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${token}`
        }
      });
      if (!response.ok) {
        throw new Error("Failed to delete note");
      }

      const noteIdKey = String(noteId);
      setMobileNotes(prev => prev.filter(note => String(note.id) !== noteIdKey));
      setMobileNoteSummaries(prev => {
        const next = { ...prev };
        delete next[noteId];
        delete next[noteIdKey];
        return next;
      });

      if (String(selectedNote?.id) === noteIdKey) {
        setSelectedNote(null);
        setShowingRefinedStaticView(false);
        setRefineUiState('idle');
        if (closeEditor) {
          setMobileEditorOpen(false);
        }
      }

      setRefreshNotes(r => !r);
      setRefreshSubgraph(r => !r);
    } catch (error) {
      window.alert("Could not delete note. Please try again.");
    } finally {
      setDeletingNoteId(null);
      setNoteRowMenuOpenId(null);
    }
  }, [deletingNoteId, selectedNote?.id, setSelectedNote]);
  const handleViewNoteConcepts = React.useCallback(async (note) => {
    if (!note?.id || note.id === 'new') return;
    setNoteRowMenuOpenId(null);
    setRefineError('');
    setRefineUiState('processing');
    try {
      const nextInsights = await fetchRefineInsights(note.id);
      setRefineInsights(nextInsights);
      setRefineInsightsNoteId(note.id);
      const normalizedNote = applyRefineState(note, inferIsRefined(note, mobileNoteSummaries));
      setSelectedNote(normalizedNote);
      setRefineUiState('results');
      setRefreshSubgraph(r => !r);
    } catch (error) {
      setRefineUiState('error');
      setRefineError('Could not load note concepts. Please try again.');
    }
  }, [applyRefineState, fetchRefineInsights, inferIsRefined, mobileNoteSummaries, setSelectedNote]);

  useEffect(() => {
    if (!selectedNote?.id || selectedNote.id === 'new') return;
    const isRefined = Boolean(
      selectedNote?.refined === true
      || selectedNote?.is_refined === true
      || selectedNote?.refine_state === 'refined'
    );
    if (!isRefined) return;
    if (String(refineInsightsNoteId) === String(selectedNote.id) && refineInsights.entities.length > 0) return;

    let isCancelled = false;
    fetchRefineInsights(selectedNote.id)
      .then((nextInsights) => {
        if (isCancelled) return;
        setRefineInsights(nextInsights);
        setRefineInsightsNoteId(selectedNote.id);
      })
      .catch(() => {});
    return () => {
      isCancelled = true;
    };
  }, [
    selectedNote?.id,
    selectedNote?.refined,
    selectedNote?.is_refined,
    selectedNote?.refine_state,
    fetchRefineInsights,
    refineInsightsNoteId,
    refineInsights.entities.length,
  ]);

  const handleNoteResultClick = (note) => {
    setHighlightQuery(activeSearchQuery);
    setLinkedConcepts(mobileSearchResults.concepts.slice(0, 4).map(concept => concept.name));
    setShowLinkedConcepts(true);
    handleSelectNote(note);
  };
  const canDeleteSelectedNote = Boolean(selectedNote?.id && selectedNote.id !== 'new');
  const selectedNoteIsRefined = Boolean(
    selectedNote?.refined === true || selectedNote?.is_refined === true || selectedNote?.refine_state === 'refined'
  );
  const canViewSelectedNoteConcepts = Boolean(
    selectedNote?.id &&
    selectedNote.id !== 'new' &&
    (
      selectedNoteIsRefined ||
      refineUiState === 'results' ||
      (refineInsights.entities.length + refineInsights.relations.length) > 0
    )
  );
  const selectedNoteRefinedEntities = React.useMemo(() => {
    if (!selectedNote?.id) return [];
    if (String(refineInsightsNoteId) !== String(selectedNote.id)) return [];
    return Array.isArray(refineInsights.entities) ? refineInsights.entities : [];
  }, [selectedNote?.id, refineInsightsNoteId, refineInsights.entities]);
  const mobileEditorLabelAction = isMobile ? (
    <div ref={editorMenuRef} style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
      <IconButton
        onClick={() => setEditorMenuOpen(open => !open)}
        size="small"
        sx={{
          color: '#492099',
          padding: '4px',
          backgroundColor: 'rgba(73, 32, 153, 0.08)',
          '&:hover': { backgroundColor: 'rgba(73, 32, 153, 0.14)' }
        }}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
      {editorMenuOpen && (
        <div
          style={{
            position: 'absolute',
            top: 'calc(100% + 8px)',
            right: 0,
            minWidth: 206,
            borderRadius: 12,
            border: '1px solid rgba(73, 32, 153, 0.12)',
            background: 'rgba(255, 255, 255, 0.97)',
            boxShadow: '0 16px 34px rgba(73, 32, 153, 0.2)',
            overflow: 'hidden',
            zIndex: 60
          }}
        >
          <button
            type="button"
            onClick={() => {
              if (isSavingDraft || isRefining) return;
              setEditorMenuOpen(false);
              handleSaveDraft();
            }}
            disabled={isSavingDraft || isRefining}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '10px 12px',
              border: 'none',
              background: 'transparent',
              color: (isSavingDraft || isRefining) ? '#AEA4C4' : '#2B2140',
              cursor: (isSavingDraft || isRefining) ? 'not-allowed' : 'pointer',
              fontSize: 13,
              fontWeight: 600
            }}
          >
            {isSavingDraft ? 'Saving draft...' : 'Save draft'}
          </button>
          <button
            type="button"
            onClick={() => {
              if (!canViewSelectedNoteConcepts || !selectedNote?.id) return;
              setEditorMenuOpen(false);
              handleViewNoteConcepts(selectedNote);
            }}
            disabled={!canViewSelectedNoteConcepts}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '10px 12px',
              border: 'none',
              borderTop: '1px solid rgba(73, 32, 153, 0.1)',
              background: 'transparent',
              color: canViewSelectedNoteConcepts ? '#2B2140' : '#AEA4C4',
              cursor: canViewSelectedNoteConcepts ? 'pointer' : 'not-allowed',
              fontSize: 13,
              fontWeight: 600
            }}
          >
            See entities / relations
          </button>
          <button
            type="button"
            onClick={() => {
              if (!canDeleteSelectedNote) return;
              setEditorMenuOpen(false);
              deleteNoteById(selectedNote.id, true);
            }}
            disabled={!canDeleteSelectedNote || deletingNoteId === selectedNote?.id}
            style={{
              width: '100%',
              textAlign: 'left',
              padding: '10px 12px',
              border: 'none',
              borderTop: '1px solid rgba(73, 32, 153, 0.1)',
              background: 'transparent',
              color: (canDeleteSelectedNote && deletingNoteId !== selectedNote?.id) ? '#C83737' : '#E1B4B4',
              cursor: (canDeleteSelectedNote && deletingNoteId !== selectedNote?.id) ? 'pointer' : 'not-allowed',
              fontSize: 13,
              fontWeight: 700
            }}
          >
            Delete note
          </button>
        </div>
      )}
    </div>
  ) : null;

  const handleConceptSelect = React.useCallback((concept) => {
    if (!concept) return;
    setMobilePrimaryView("home");
    setSelectedConcept(concept);
    setSearchPage("concept");
    if (isMobile) {
      setMobileEditorOpen(false);
      if (location.pathname === '/newnote') {
        navigate('/app');
      }
    }
  }, [isMobile, location.pathname, navigate]);

  const handleExploreConceptClick = React.useCallback((conceptName) => {
    if (!conceptName) return;
    const normalized = String(conceptName).toLowerCase().trim();
    const match = conceptNodes.find((concept) => (
      String(concept.name || '').toLowerCase().trim() === normalized
      || String(concept.id || '').toLowerCase().trim() === normalized
    ));
    handleConceptSelect(match || { id: conceptName, name: conceptName, type: 'Topic' });
  }, [conceptNodes, handleConceptSelect]);

  const handleDesktopGraphOpenConcept = React.useCallback((conceptName) => {
    closeDesktopFullGraph();
    if (!conceptName) return;
    const normalizedConcept = conceptName.toLowerCase().trim();
    const matchedConcept = conceptNodes.find((concept) => (
      String(concept.name || '').toLowerCase().trim() === normalizedConcept
      || String(concept.id || '').toLowerCase().trim() === normalizedConcept
    ));
    handleConceptSelect(matchedConcept || { id: conceptName, name: conceptName, type: "Topic" });
  }, [closeDesktopFullGraph, conceptNodes, handleConceptSelect]);

  const handleSearchInputChange = (event) => {
    setMobileSearchValue(event.target.value);
    if (searchPage === "results") {
      setSearchPage("home");
      setSearchQueryCommitted("");
    }
    if (selectedConcept) {
      setSelectedConcept(null);
    }
  };

  const handleSearchSubmit = () => {
    if (!searchInputValue) return;
    setSearchQueryCommitted(searchInputValue);
    setSearchPage("results");
  };

  const handleClearSearch = () => {
    setMobileSearchValue("");
    setSearchQueryCommitted("");
    setSearchPage("home");
    setSelectedConcept(null);
  };

  const buildGraphState = (centerConcept, breadcrumb) => ({
    centerConcept,
    originConcept: centerConcept,
    breadcrumb,
  });

  useEffect(() => {
    if (!isMobile) return;
    if (location.pathname === '/newnote' && !showEditorOnMobile) {
      setMobileEditorOpen(true);
    }
  }, [isMobile, location.pathname, showEditorOnMobile]);

  useEffect(() => {
    if (!isMobile) return;
    if (location.pathname === '/app' && showEditorOnMobile) {
      setMobileEditorOpen(false);
    }
  }, [isMobile, location.pathname, showEditorOnMobile]);

  useEffect(() => {
    if (!editorMenuOpen) return;
    const handleOutside = (event) => {
      if (editorMenuRef.current && !editorMenuRef.current.contains(event.target)) {
        setEditorMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [editorMenuOpen]);

  useEffect(() => {
    if (!mobileHeaderMenuOpen) return;
    const handleOutside = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.nr-brand-menu-wrap')) return;
      setMobileHeaderMenuOpen(false);
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [mobileHeaderMenuOpen]);

  useEffect(() => {
    if (!isMobile || showEditorOnMobile) return;
    const active = document.activeElement;
    if (active && typeof active.blur === 'function') {
      active.blur();
    }
  }, [isMobile, showEditorOnMobile]);

  useEffect(() => {
    if (!isMobile || !showEditorOnMobile) return;
    const timer = setTimeout(() => {
      editorRef.current?.focusEditor?.();
    }, 0);
    return () => clearTimeout(timer);
  }, [isMobile, showEditorOnMobile]);

  useEffect(() => {
    if (!notesSortMenuOpen && !homeMoreMenuOpen) return;
    const handlePointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.nr-sort-wrap') || target.closest('.nr-filter-other-wrap')) return;
      setNotesSortMenuOpen(false);
      setHomeMoreMenuOpen(false);
    };
    document.addEventListener('pointerdown', handlePointerDown);
    return () => document.removeEventListener('pointerdown', handlePointerDown);
  }, [notesSortMenuOpen, homeMoreMenuOpen]);

  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const updateKeyboard = () => {
      const occluded = window.innerHeight - (viewport.height + viewport.offsetTop);
      setKeyboardVisible(occluded > 80);
    };
    updateKeyboard();
    viewport.addEventListener('resize', updateKeyboard);
    viewport.addEventListener('scroll', updateKeyboard);
    return () => {
      viewport.removeEventListener('resize', updateKeyboard);
      viewport.removeEventListener('scroll', updateKeyboard);
    };
  }, []);

  const showSearchWorkspace = searchInputValue.length > 0 && !selectedConcept && searchPage !== "results";
  const showSearchResultsPage = searchPage === "results" && activeSearchQuery.length > 0;
  const isSearching = searchInputValue.length > 0 || searchPage === "results" || !!selectedConcept;
  const showConceptPage = Boolean(selectedConcept);

  const conceptNoteMatches = React.useMemo(() => {
    if (!selectedConcept) return [];
    const name = selectedConcept.name.toLowerCase();
    return mobileNotes
      .map(note => {
        const text = `${note.title || ""} ${extractNoteText(note)}`.toLowerCase();
        const mentionCount = text.split(name).length - 1;
        return { ...note, mentionCount };
      })
      .filter(note => note.mentionCount > 0)
      .sort((a, b) => b.mentionCount - a.mentionCount);
  }, [selectedConcept, mobileNotes]);

  const relatedConcepts = React.useMemo(() => {
    if (!selectedConcept || !graphData?.edges) return [];
    const related = new Set();
    graphData.edges.forEach(edge => {
      const from = edge.source || edge.from;
      const to = edge.target || edge.to;
      if (from === selectedConcept.name) related.add(to);
      if (to === selectedConcept.name) related.add(from);
    });
    return conceptNodes.filter(concept => related.has(concept.name)).slice(0, 10);
  }, [selectedConcept, graphData, conceptNodes]);

  const conceptGraphPreview = React.useMemo(() => {
    if (!selectedConcept?.name) return null;
    const centerName = selectedConcept.name;
    const edges = Array.isArray(graphData?.edges) ? graphData.edges : [];
    const neighborStrength = new Map();

    edges.forEach((edge) => {
      const from = edge.source || edge.from;
      const to = edge.target || edge.to;
      if (!from || !to) return;
      if (from === centerName && to !== centerName) {
        neighborStrength.set(to, (neighborStrength.get(to) || 0) + 1);
      }
      if (to === centerName && from !== centerName) {
        neighborStrength.set(from, (neighborStrength.get(from) || 0) + 1);
      }
    });

    let neighbors = Array.from(neighborStrength.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
      .slice(0, 4)
      .map(([name]) => name);

    if (!neighbors.length) {
      neighbors = relatedConcepts.slice(0, 4).map(concept => concept.name);
    }

    const centerNode = { id: centerName, x: 160, y: 70, isCenter: true };
    const ringSlots = [
      { x: 160, y: 22 },
      { x: 62, y: 52 },
      { x: 258, y: 52 },
      { x: 160, y: 118 }
    ];
    const previewNodes = [centerNode, ...neighbors.map((name, index) => ({
      id: name,
      x: ringSlots[index]?.x ?? 160,
      y: ringSlots[index]?.y ?? 70,
      isCenter: false
    }))];

    const nodeIds = new Set(previewNodes.map(node => node.id));
    const previewEdges = [];
    const seenEdgeKeys = new Set();

    // Always include center-to-neighbor edges for readable preview.
    neighbors.forEach((name) => {
      const key = [centerName, name].sort().join('::');
      seenEdgeKeys.add(key);
      previewEdges.push({ from: centerName, to: name, isPrimary: true });
    });

    // Add additional real edges between included nodes.
    edges.forEach((edge) => {
      const from = edge.source || edge.from;
      const to = edge.target || edge.to;
      if (!from || !to) return;
      if (!nodeIds.has(from) || !nodeIds.has(to) || from === to) return;
      const key = [from, to].sort().join('::');
      if (seenEdgeKeys.has(key)) return;
      seenEdgeKeys.add(key);
      previewEdges.push({ from, to, isPrimary: false });
    });

    return { nodes: previewNodes, edges: previewEdges };
  }, [selectedConcept?.name, graphData?.edges, relatedConcepts]);

  const wrapPreviewLabel = React.useCallback((value, maxCharsPerLine, maxLines = 2) => {
    const text = String(value || '').trim();
    if (!text) return [''];
    const words = text.split(/\s+/);
    const lines = [];
    let current = '';

    words.forEach((word) => {
      if (!word) return;
      const candidate = current ? `${current} ${word}` : word;
      if (candidate.length <= maxCharsPerLine) {
        current = candidate;
        return;
      }
      if (current) {
        lines.push(current);
      }
      current = word;
    });
    if (current) lines.push(current);

    // Fallback for long single tokens.
    if (!lines.length && text.length) {
      for (let i = 0; i < text.length && lines.length < maxLines; i += maxCharsPerLine) {
        lines.push(text.slice(i, i + maxCharsPerLine));
      }
    }

    const bounded = lines.slice(0, maxLines);
    if (lines.length > maxLines && bounded.length) {
      const last = bounded[bounded.length - 1];
      bounded[bounded.length - 1] = `${last.slice(0, Math.max(0, maxCharsPerLine - 1))}…`;
    }
    return bounded;
  }, []);

  const conceptLastMention = React.useMemo(() => {
    if (!conceptNoteMatches.length) return null;
    const dates = conceptNoteMatches
      .map(note => new Date(note.modified_at || note.date || note.updated_at || note.created_at || 0))
      .filter(date => !Number.isNaN(date.getTime()));
    if (!dates.length) return null;
    return new Date(Math.max(...dates.map(date => date.getTime())));
  }, [conceptNoteMatches]);
  const filteredSortedNotes = React.useMemo(() => {
    const query = notesSearchValue.trim().toLowerCase();
    const getNoteDate = (note) => {
      const raw = note?.modified_at || note?.updated_at || note?.created_at || note?.date;
      const parsed = raw ? new Date(raw).getTime() : 0;
      return Number.isNaN(parsed) ? 0 : parsed;
    };
    const getSummary = (note) => {
      const aiSummary = getAiSummaryForNote(note);
      if (aiSummary) return aiSummary;
      return getNoteSnippet(extractNoteText(note), "");
    };

    return mobileNotes
      .filter(note => {
        if (!query) return true;
        const title = (note?.title || "").toLowerCase();
        const body = extractNoteText(note).toLowerCase();
        const summary = getSummary(note).toLowerCase();
        return title.includes(query) || body.includes(query) || summary.includes(query);
      })
      .sort((a, b) => {
        const direction = notesSortOrder === "oldest" ? 1 : -1;
        return direction * (getNoteDate(a) - getNoteDate(b));
      });
  }, [mobileNotes, notesSearchValue, notesSortOrder, getAiSummaryForNote]);

  const closeRefinePanel = () => {
    if (isRefining) return;
    setRefineUiState('idle');
    setRefineError('');
  };

  const refinePanelBottom = isMobile
    ? `calc(env(safe-area-inset-bottom) + ${keyboardVisible ? 12 : 102}px)`
    : '24px';

  const refinePanel = (showRefinePanel && !showDesktopGraphInPane) ? (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: isMobile ? 12 : 'auto',
        right: 12,
        bottom: refinePanelBottom,
        width: isMobile ? 'calc(100% - 24px)' : 420,
        maxHeight: isMobile ? '58vh' : '64vh',
        overflowY: 'auto',
        background: 'rgba(255, 255, 255, 0.96)',
        border: '1px solid rgba(73, 32, 153, 0.18)',
        borderRadius: 18,
        boxShadow: '0 22px 38px rgba(73, 32, 153, 0.2)',
        zIndex: 32,
        backdropFilter: 'blur(8px)'
      }}
    >
      <div style={{ padding: '14px 14px 12px', borderBottom: '1px solid rgba(73, 32, 153, 0.12)' }}>
        <div style={{ fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6e5e8d', fontWeight: 700 }}>
          Note Refinery
        </div>
        <div style={{ marginTop: 2, fontSize: 18, fontWeight: 700, color: '#2b2140' }}>
          {refineUiState === 'processing' ? 'Refining your note...' : 'Refinement complete'}
        </div>
      </div>

      {refineUiState === 'processing' && (
        <div style={{ padding: '14px 14px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="nr-refine-animation" aria-hidden="true">
              <div className="nr-refine-stream nr-refine-stream--one" />
              <div className="nr-refine-stream nr-refine-stream--two" />
              <div className="nr-refine-stream nr-refine-stream--three" />
              <div className="nr-refine-core" />
            </div>
            <div style={{ color: '#564572', fontSize: 14, lineHeight: 1.4 }}>
              Distilling concepts and relationship links from this note.
            </div>
          </div>
        </div>
      )}

      {refineUiState === 'error' && (
        <div style={{ padding: 14 }}>
          <div style={{ color: '#922f2f', fontSize: 14, marginBottom: 10 }}>{refineError}</div>
          <button
            type="button"
            onClick={handleRefine}
            style={{
              border: 'none',
              borderRadius: 12,
              background: '#492099',
              color: '#fff',
              fontWeight: 600,
              fontSize: 13,
              padding: '10px 14px',
              cursor: 'pointer'
            }}
          >
            Retry refine
          </button>
        </div>
      )}

      {refineUiState === 'results' && (
        <div style={{ padding: '14px 14px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6e5e8d', fontWeight: 700 }}>
              Entities ({refineInsights.entities.length})
            </div>
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {refineInsights.entities.length > 0 ? refineInsights.entities.map((entity, index) => (
                <button
                  key={entity.id}
                  type="button"
                  onClick={() => handleConceptSelect(entity)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 999,
                    background: 'rgba(73, 32, 153, 0.08)',
                    border: '1px solid rgba(73, 32, 153, 0.18)',
                    color: '#492099',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    animation: `nrReveal 260ms ease both`,
                    animationDelay: `${Math.min(index * 50, 300)}ms`
                  }}
                >
                  {entity.name}
                </button>
              )) : (
                <div style={{ fontSize: 13, color: '#7b6d96' }}>No entities found yet.</div>
              )}
            </div>
          </div>

          <div>
            <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#6e5e8d', fontWeight: 700 }}>
              Relations ({refineInsights.relations.length})
            </div>
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {refineInsights.relations.length > 0 ? refineInsights.relations.map((relation, index) => (
                <div
                  key={relation.id}
                  style={{
                    borderRadius: 12,
                    border: '1px solid rgba(73, 32, 153, 0.12)',
                    background: '#fbf9ff',
                    padding: '9px 10px',
                    fontSize: 13,
                    color: '#3f2f5f',
                    animation: `nrReveal 300ms ease both`,
                    animationDelay: `${Math.min(120 + (index * 60), 480)}ms`
                  }}
                >
                  <strong>{relation.source}</strong> <span style={{ color: '#7a67a1' }}>{relation.label}</span> <strong>{relation.target}</strong>
                </div>
              )) : (
                <div style={{ fontSize: 13, color: '#7b6d96' }}>No relations found yet.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {refineUiState !== 'processing' && (
        <div style={{ padding: '0 14px 14px', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={closeRefinePanel}
            style={{
              border: '1px solid rgba(73, 32, 153, 0.2)',
              borderRadius: 12,
              background: '#fff',
              color: '#492099',
              fontWeight: 600,
              fontSize: 13,
              padding: '8px 12px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      )}
    </div>
  ) : null;

  // Render Mobile Layout
  if (isMobile) {
    return (
      <div id="note-view-container-mobile" style={{ display: 'flex', width: '100%', height: isMobileEditorRoute ? '100vh' : 'calc(100vh - 56px)', flexDirection: 'column', position: 'relative', overflow: 'hidden' }}>
        <style>
          {`
            #note-view-container-mobile {
              --nr-ink: #2b2140;
              --nr-ink-soft: #5a4a7b;
              --nr-accent: #5c3b8b;
              --nr-accent-dark: #492099;
              --nr-surface: #ffffff;
              --nr-surface-muted: #f7f4fb;
              --nr-border: rgba(73, 32, 153, 0.16);
              --nr-shadow: 0 18px 35px rgba(73, 32, 153, 0.18);
            }
            #note-view-container-mobile .nr-home-screen {
              position: relative;
              height: 100%;
              width: 100%;
              overflow: hidden;
              background: linear-gradient(180deg, #f7f4fb 0%, #ffffff 52%, #f2eef9 100%);
              display: flex;
              flex-direction: column;
            }
            #note-view-container-mobile .nr-home-bg {
              position: absolute;
              inset: 0;
              pointer-events: none;
            }
            #note-view-container-mobile .nr-orb {
              position: absolute;
              border-radius: 999px;
              background: radial-gradient(circle, rgba(92, 59, 139, 0.18) 0%, rgba(92, 59, 139, 0) 70%);
              filter: blur(0.5px);
              opacity: 0.75;
            }
            #note-view-container-mobile .nr-orb--one {
              width: 220px;
              height: 220px;
              top: -80px;
              right: -40px;
            }
            #note-view-container-mobile .nr-orb--two {
              width: 260px;
              height: 260px;
              bottom: 120px;
              left: -120px;
            }
            #note-view-container-mobile .nr-home-content {
              position: relative;
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 18px;
              padding: 16px 22px 120px;
              overflow-y: auto;
            }
            #note-view-container-mobile .nr-brand {
              display: flex;
              align-items: center;
              justify-content: space-between;
              color: var(--nr-accent-dark);
              font-weight: 600;
              font-size: 20px;
              letter-spacing: 0.02em;
              margin-top: -2px;
              animation: nrFadeUp 420ms ease both;
            }
            #note-view-container-mobile .nr-brand-main {
              display: flex;
              align-items: center;
              margin-left: -4px;
            }
            #note-view-container-mobile .nr-brand span {
              margin-top: 8px;
            }
            #note-view-container-mobile .nr-brand img {
              width: 22px;
              height: 22px;
            }
            #note-view-container-mobile .nr-brand-menu-wrap {
              position: relative;
              display: inline-flex;
              align-items: center;
              z-index: 90;
            }
            #note-view-container-mobile .nr-header-menu-btn {
              border: 1px solid rgba(73, 32, 153, 0.2);
              background: rgba(255, 255, 255, 0.72);
              color: var(--nr-accent-dark);
              border-radius: 999px;
              width: 34px;
              height: 34px;
              padding: 0;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              cursor: pointer;
              font-family: inherit;
            }
            #note-view-container-mobile .nr-home-title {
              font-size: 28px;
              font-weight: 600;
              line-height: 1.2;
              color: var(--nr-ink);
              margin: 10px 0 0;
              animation: nrFadeUp 460ms ease both;
              animation-delay: 60ms;
            }
            #note-view-container-mobile .nr-search {
              display: flex;
              align-items: center;
              gap: 10px;
              background: var(--nr-surface);
              border-radius: 999px;
              padding: 12px 16px;
              border: 1px solid var(--nr-border);
              box-shadow: 0 10px 25px rgba(73, 32, 153, 0.08);
              animation: nrFadeUp 520ms ease both;
              animation-delay: 120ms;
            }
            #note-view-container-mobile .nr-search input {
              border: none;
              outline: none;
              font-size: 16px;
              flex: 1;
              color: var(--nr-ink);
              background: transparent;
              font-family: inherit;
            }
            #note-view-container-mobile .nr-search input::placeholder {
              color: #8a7ea6;
            }
            #note-view-container-mobile .nr-clear-btn {
              border: none;
              background: none;
              color: var(--nr-accent-dark);
              font-size: 13px;
              font-weight: 600;
              cursor: pointer;
            }
            #note-view-container-mobile .nr-explore {
              display: flex;
              flex-direction: column;
              gap: 12px;
              flex: 1;
              min-height: 0;
              animation: nrFadeUp 560ms ease both;
              animation-delay: 180ms;
            }
            #note-view-container-mobile .nr-explore-label {
              text-transform: uppercase;
              font-size: 12px;
              letter-spacing: 0.18em;
              color: var(--nr-ink-soft);
              font-weight: 600;
            }
            #note-view-container-mobile .nr-chip-row {
              display: flex;
              flex-wrap: wrap;
              gap: 10px;
            }
            #note-view-container-mobile .nr-filter-row {
              margin-top: 6px;
              display: flex;
              align-items: flex-start;
              flex-direction: column;
              gap: 10px;
              margin-bottom: 6px;
              padding-bottom: 2px;
            }
            #note-view-container-mobile .nr-filter-label {
              font-size: 12px;
              color: #7c6f96;
              font-weight: 600;
            }
            #note-view-container-mobile .nr-filter-clear {
              border: none;
              background: none;
              color: #6f5a97;
              font-size: 12px;
              font-weight: 600;
              text-decoration: underline;
              text-underline-offset: 3px;
              cursor: pointer;
              padding: 0;
            }
            #note-view-container-mobile .nr-filter-buttons {
              display: flex;
              flex-wrap: wrap;
              gap: 8px;
              width: 100%;
            }
            #note-view-container-mobile .nr-filter-btn {
              border: 1px dotted rgba(73, 32, 153, 0.5);
              border-radius: 999px;
              background: transparent;
              color: #492099;
              font-size: 12px;
              font-weight: 600;
              padding: 5px 12px;
              cursor: pointer;
              display: inline-flex;
              align-items: center;
              gap: 6px;
            }
            #note-view-container-mobile .nr-filter-btn.active {
              background: transparent;
            }
            #note-view-container-mobile .nr-filter-other-wrap {
              margin-left: auto;
              display: flex;
              align-items: center;
              gap: 6px;
              position: relative;
            }
            #note-view-container-mobile .nr-more-link {
              border: none;
              background: none;
              color: #6f5a97;
              font-size: 12px;
              font-weight: 700;
              text-decoration: underline;
              text-underline-offset: 3px;
              cursor: pointer;
              padding: 0;
            }
            #note-view-container-mobile .nr-dotted-select {
              border: 1px dotted rgba(73, 32, 153, 0.5);
              border-radius: 999px;
              background: transparent;
              color: #492099;
              font-size: 11px;
              font-weight: 600;
              padding: 3px 8px;
              max-width: 120px;
            }
            #note-view-container-mobile .nr-sort-wrap {
              margin-top: 8px;
              display: flex;
              justify-content: flex-end;
              position: relative;
            }
            #note-view-container-mobile .nr-sort-link {
              border: none;
              background: none;
              padding: 0;
              color: #492099;
              font-size: 13px;
              font-weight: 600;
              text-decoration: underline;
              text-underline-offset: 3px;
              cursor: pointer;
            }
            #note-view-container-mobile .nr-sort-dropdown {
              position: absolute;
              top: 24px;
              right: 0;
              z-index: 140;
              min-width: 150px;
              border: 1px solid rgba(73, 32, 153, 0.2);
              border-radius: 10px;
              background: #fff;
              box-shadow: 0 10px 20px rgba(73, 32, 153, 0.14);
              overflow: hidden;
            }
            #note-view-container-mobile .nr-menu-option {
              width: 100%;
              border: none;
              background: none;
              text-align: left;
              padding: 10px 12px;
              color: #4b3c67;
              font-size: 13px;
              font-weight: 600;
              cursor: pointer;
            }
            #note-view-container-mobile .nr-menu-option.active {
              background: rgba(73, 32, 153, 0.08);
              color: #492099;
            }
            #note-view-container-mobile .nr-menu-empty {
              padding: 10px 12px;
              color: #8b7fa3;
              font-size: 12px;
            }
            #note-view-container-mobile .nr-explore-cloud {
              position: relative;
              flex: 1;
              min-height: 240px;
              max-height: calc(100dvh - 360px);
              overflow-y: auto;
              overflow-x: hidden;
              padding-right: 2px;
              scrollbar-width: thin;
              scrollbar-color: rgba(73, 32, 153, 0.28) transparent;
            }
            #note-view-container-mobile .nr-explore-cloud::-webkit-scrollbar {
              width: 6px;
            }
            #note-view-container-mobile .nr-explore-cloud::-webkit-scrollbar-thumb {
              background: rgba(73, 32, 153, 0.28);
              border-radius: 999px;
            }
            #note-view-container-mobile .nr-explore-cloud::-webkit-scrollbar-track {
              background: transparent;
            }
            #note-view-container-mobile .nr-explore-fade {
              position: sticky;
              bottom: 0;
              height: 112px;
              margin-top: -112px;
              pointer-events: none;
              background: linear-gradient(180deg, rgba(247, 244, 251, 0) 0%, rgba(247, 244, 251, 0.85) 55%, rgba(247, 244, 251, 1) 100%);
              z-index: 1;
            }
            #note-view-container-mobile .nr-chip {
              border-radius: 999px;
              padding: 8px 16px;
              border: 1px solid rgba(73, 32, 153, 0.18);
              background: rgba(92, 59, 139, 0.08);
              color: var(--nr-accent-dark);
              font-size: 14px;
              font-weight: 500;
              cursor: pointer;
              transition: transform 140ms ease, box-shadow 140ms ease;
              font-family: inherit;
            }
            #note-view-container-mobile .nr-chip:active {
              transform: translateY(1px);
              box-shadow: 0 6px 12px rgba(73, 32, 153, 0.16);
            }
            #note-view-container-mobile .nr-fab {
              position: fixed;
              left: 50%;
              bottom: calc(env(safe-area-inset-bottom) + 78px);
              transform: translateX(-50%);
              width: 74px;
              height: 74px;
              border-radius: 50%;
              border: none;
              background: linear-gradient(135deg, #D7C4FA 0%, #8C60E6 45%, #4B1FC5 100%);
              color: white;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 12px 26px rgba(90, 50, 186, 0.38);
              cursor: pointer;
              z-index: 20;
              transition: transform 120ms ease, box-shadow 120ms ease;
            }
            #note-view-container-mobile .nr-fab.nr-fab--searching {
              left: auto;
              right: 14px;
              bottom: calc(env(safe-area-inset-bottom) + 68px);
              transform: none;
              width: 48px;
              height: 48px;
              opacity: 0.4;
              box-shadow: 0 10px 18px rgba(73, 32, 153, 0.18);
            }
            #note-view-container-mobile .nr-fab:active {
              transform: translateX(-50%) scale(0.98);
            }
            #note-view-container-mobile .nr-fab.nr-fab--searching:active {
              transform: scale(0.96);
            }
            #note-view-container-mobile .nr-bottom-nav {
              position: fixed;
              left: 0;
              right: 0;
              bottom: 0;
              padding: 10px 16px calc(env(safe-area-inset-bottom) + 12px);
              display: flex;
              justify-content: space-around;
              background: rgba(255, 255, 255, 0.9);
              border-top: 1px solid rgba(18, 50, 53, 0.08);
              backdrop-filter: blur(10px);
              z-index: 10;
            }
            #note-view-container-mobile .nr-nav-item {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 4px;
              background: none;
              border: none;
              color: #9a90b4;
              font-size: 12px;
              font-weight: 500;
              cursor: pointer;
              font-family: inherit;
            }
            #note-view-container-mobile .nr-nav-item.active {
              color: var(--nr-accent-dark);
            }
            #note-view-container-mobile .nr-search-shell {
              background: var(--nr-surface);
              border-radius: 20px;
              padding: 14px;
              border: 1px solid rgba(73, 32, 153, 0.08);
              box-shadow: 0 16px 26px rgba(73, 32, 153, 0.08);
              display: flex;
              flex-direction: column;
              gap: 18px;
              animation: nrFadeUp 620ms ease both;
              animation-delay: 140ms;
            }
            #note-view-container-mobile .nr-section {
              display: flex;
              flex-direction: column;
              gap: 10px;
            }
            #note-view-container-mobile .nr-section-title {
              font-size: 12px;
              text-transform: uppercase;
              letter-spacing: 0.2em;
              color: #7c6f96;
              font-weight: 700;
            }
            #note-view-container-mobile .nr-row {
              display: flex;
              align-items: flex-start;
              gap: 12px;
              padding: 10px 12px;
              border-radius: 14px;
              border: 1px solid rgba(73, 32, 153, 0.08);
              background: #fbf9ff;
              cursor: pointer;
              transition: transform 140ms ease, box-shadow 140ms ease;
            }
            #note-view-container-mobile .nr-row:active {
              transform: translateY(1px);
              box-shadow: 0 8px 16px rgba(73, 32, 153, 0.12);
            }
            #note-view-container-mobile .nr-row-main {
              flex: 1;
              display: flex;
              flex-direction: column;
              gap: 4px;
              min-width: 0;
            }
            #note-view-container-mobile .nr-row-title {
              font-size: 15px;
              font-weight: 600;
              color: var(--nr-ink);
            }
            #note-view-container-mobile .nr-row-meta {
              font-size: 13px;
              color: #6e5e8d;
              line-height: 1.35;
            }
            #note-view-container-mobile .nr-row-icon {
              width: 32px;
              height: 32px;
              border-radius: 10px;
              background: rgba(73, 32, 153, 0.1);
              color: var(--nr-accent-dark);
              display: inline-flex;
              align-items: center;
              justify-content: center;
              flex-shrink: 0;
            }
            #note-view-container-mobile .nr-action {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 10px;
              padding: 12px 14px;
              border-radius: 14px;
              border: 1px dashed rgba(73, 32, 153, 0.25);
              background: rgba(73, 32, 153, 0.06);
              font-weight: 600;
              color: var(--nr-accent-dark);
              cursor: pointer;
              font-size: 14px;
            }
            #note-view-container-mobile .nr-action:active {
              transform: translateY(1px);
            }
            #note-view-container-mobile .nr-search-empty {
              font-size: 14px;
              color: #8b7fa3;
              text-align: center;
            }
            #note-view-container-mobile .nr-concept-page {
              display: flex;
              flex-direction: column;
              gap: 18px;
              padding: 14px 0 40px;
            }
            #note-view-container-mobile .nr-summary-strip {
              padding: 14px 16px;
              border-radius: 18px;
              background: rgba(73, 32, 153, 0.08);
              display: flex;
              flex-direction: column;
              gap: 6px;
            }
            #note-view-container-mobile .nr-summary-title {
              font-size: 20px;
              font-weight: 700;
              color: var(--nr-ink);
            }
            #note-view-container-mobile .nr-summary-meta {
              font-size: 13px;
              color: #5f4b7b;
              display: flex;
              flex-wrap: wrap;
              gap: 10px;
            }
            #note-view-container-mobile .nr-chip-row.scroll {
              flex-wrap: nowrap;
              overflow-x: auto;
              padding-bottom: 6px;
            }
            #note-view-container-mobile .nr-mini-graph {
              background: #fff;
              border-radius: 18px;
              border: 1px solid rgba(73, 32, 153, 0.1);
              padding: 10px;
            }
            #note-view-container-mobile .nr-full-graph {
              margin-top: 10px;
              border: none;
              width: 100%;
              padding: 12px 14px;
              border-radius: 14px;
              background: var(--nr-accent-dark);
              color: #fff;
              font-weight: 600;
              cursor: pointer;
            }
            #note-view-container-mobile .nr-full-graph[disabled] {
              background: #b9a9d6;
              cursor: not-allowed;
            }
            @keyframes nrFadeUp {
              from {
                opacity: 0;
                transform: translateY(12px);
              }
              to {
                opacity: 1;
                transform: translateY(0);
              }
            }
            @keyframes refinePulse {
              0% {
                box-shadow: 0 8px 18px rgba(73, 32, 153, 0.22);
                filter: drop-shadow(0 0 0 rgba(73, 32, 153, 0));
                opacity: 1;
              }
              50% {
                box-shadow: 0 14px 26px rgba(73, 32, 153, 0.42);
                filter: drop-shadow(0 0 6px rgba(73, 32, 153, 0.22));
                opacity: 0.95;
              }
              100% {
                box-shadow: 0 8px 18px rgba(73, 32, 153, 0.22);
                filter: drop-shadow(0 0 0 rgba(73, 32, 153, 0));
                opacity: 1;
              }
            }
            @keyframes nrStreamFlow {
              0% { transform: translateX(-2px) scale(0.8); opacity: 0.35; }
              50% { transform: translateX(8px) scale(1); opacity: 1; }
              100% { transform: translateX(18px) scale(0.7); opacity: 0.3; }
            }
            @keyframes nrCorePulse {
              0% { transform: scale(0.94); box-shadow: 0 0 0 rgba(73, 32, 153, 0.2); }
              50% { transform: scale(1.06); box-shadow: 0 0 0 8px rgba(73, 32, 153, 0.05); }
              100% { transform: scale(0.94); box-shadow: 0 0 0 rgba(73, 32, 153, 0.2); }
            }
            @keyframes nrReveal {
              from { opacity: 0; transform: translateY(8px); }
              to { opacity: 1; transform: translateY(0); }
            }
            #note-view-container-mobile .nr-refine-animation {
              width: 64px;
              height: 48px;
              position: relative;
              border-radius: 14px;
              border: 1px solid rgba(73, 32, 153, 0.16);
              background: linear-gradient(180deg, #f6f1fd 0%, #ffffff 100%);
              overflow: hidden;
              flex-shrink: 0;
            }
            #note-view-container-mobile .nr-refine-core {
              position: absolute;
              right: 6px;
              top: 50%;
              width: 16px;
              height: 16px;
              margin-top: -8px;
              border-radius: 50%;
              background: #492099;
              animation: nrCorePulse 1.3s ease-in-out infinite;
            }
            #note-view-container-mobile .nr-refine-stream {
              position: absolute;
              left: 8px;
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background: rgba(73, 32, 153, 0.7);
              animation: nrStreamFlow 1.2s ease-in-out infinite;
            }
            #note-view-container-mobile .nr-refine-stream--one { top: 11px; }
            #note-view-container-mobile .nr-refine-stream--two { top: 20px; animation-delay: 0.16s; }
            #note-view-container-mobile .nr-refine-stream--three { top: 29px; animation-delay: 0.3s; }
            @media (prefers-reduced-motion: reduce) {
              #note-view-container-mobile .nr-refine-stream,
              #note-view-container-mobile .nr-refine-core,
              #note-view-container-mobile [style*="nrReveal"],
              #note-view-container-mobile [style*="refinePulse"] {
                animation: none !important;
              }
            }
          `}
        </style>
        {/* Mobile: List View (Recent Notes + Subgraph) */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          opacity: showEditorOnMobile ? 0 : 1,
          pointerEvents: showEditorOnMobile ? 'none' : 'auto',
          transition: 'opacity 160ms ease'
        }}>
          <div className="nr-home-screen">
            <div className="nr-home-bg">
              <div className="nr-orb nr-orb--one" />
              <div className="nr-orb nr-orb--two" />
            </div>
            <div className="nr-home-content">
              <div className="nr-brand">
                <div className="nr-brand-main">
                  <img
                    src="/logo2-removebg-preview.png"
                    alt="NoteRefinery logo"
                    style={{ width: 42, height: 42 }}
                  />
                  <span>NoteRefinery</span>
                </div>
                <div className="nr-brand-menu-wrap">
                  <button
                    type="button"
                    className="nr-header-menu-btn"
                    onClick={() => setMobileHeaderMenuOpen(open => !open)}
                    aria-label="Open menu"
                    aria-haspopup="menu"
                    aria-expanded={mobileHeaderMenuOpen}
                  >
                    <MenuIcon sx={{ fontSize: 18 }} />
                  </button>
                  {mobileHeaderMenuOpen && (
                    <div className="nr-sort-dropdown" role="menu" aria-label="Account menu" style={{ top: 40, right: 0 }}>
                      <button
                        type="button"
                        className="nr-menu-option"
                        onClick={() => {
                          setMobileHeaderMenuOpen(false);
                          handleLogout();
                        }}
                      >
                        Log out
                      </button>
                    </div>
                  )}
                </div>
              </div>
              {mobilePrimaryView === "notes" ? (
                <div className="nr-search-shell">
                  <div className="nr-section">
                    <span className="nr-section-title">Your Notes</span>
                    <div className="nr-search">
                      <SearchIcon sx={{ color: 'var(--nr-accent-dark)' }} />
                      <input
                        type="text"
                        placeholder="Search notes by title or summary..."
                        value={notesSearchValue}
                        onChange={(event) => setNotesSearchValue(event.target.value)}
                        aria-label="Search your notes"
                      />
                      {notesSearchValue && (
                        <button type="button" className="nr-clear-btn" onClick={() => setNotesSearchValue("")}>
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="nr-sort-wrap">
                      <button
                        type="button"
                        className="nr-sort-link"
                        onClick={() => setNotesSortMenuOpen(open => !open)}
                        aria-haspopup="menu"
                        aria-expanded={notesSortMenuOpen}
                      >
                        Sort by
                      </button>
                      {notesSortMenuOpen && (
                        <div className="nr-sort-dropdown" role="menu" aria-label="Sort notes">
                          <button
                            type="button"
                            className={`nr-menu-option ${notesSortOrder === "newest" ? "active" : ""}`}
                            onClick={() => {
                              setNotesSortOrder("newest");
                              setNotesSortMenuOpen(false);
                            }}
                          >
                            Newest first
                          </button>
                          <button
                            type="button"
                            className={`nr-menu-option ${notesSortOrder === "oldest" ? "active" : ""}`}
                            onClick={() => {
                              setNotesSortOrder("oldest");
                              setNotesSortMenuOpen(false);
                            }}
                          >
                            Oldest first
                          </button>
                        </div>
                      )}
                    </div>
                    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {filteredSortedNotes.length > 0 ? filteredSortedNotes.map(note => {
                        const summary = getAiSummaryForNote(note) || getNoteSnippet(extractNoteText(note), "") || "No summary yet.";
                        const noteDate = new Date(note.modified_at || note.updated_at || note.created_at || note.date || 0);
                        const dateText = Number.isNaN(noteDate.getTime()) ? "" : noteDate.toLocaleDateString();
                        return (
                          <div key={note.id} className="nr-row" style={{ position: 'relative' }} onClick={() => { setNoteRowMenuOpenId(null); handleSelectNote(note); }}>
                            <div className="nr-row-icon">
                              <DescriptionOutlinedIcon fontSize="small" />
                            </div>
                            <div className="nr-row-main">
                              <div className="nr-row-title">{note.title || "Untitled note"}</div>
                              <div className="nr-row-meta">{summary}</div>
                              {dateText && (
                                <div className="nr-row-meta" style={{ marginTop: 4, fontSize: 12, color: '#8b7fa3' }}>{dateText}</div>
                              )}
                            </div>
                            <button
                              type="button"
                              onClick={(event) => {
                                event.stopPropagation();
                                setNoteRowMenuOpenId(current => (current === note.id ? null : note.id));
                              }}
                              aria-label="More options"
                              style={{
                                border: 'none',
                                background: 'none',
                                color: '#6f5a97',
                                cursor: 'pointer',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: 0,
                                marginRight: 6
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </button>
                            {noteRowMenuOpenId === note.id && (
                              <div
                                onClick={(event) => event.stopPropagation()}
                                style={{
                                  position: 'absolute',
                                  right: 28,
                                  top: 34,
                                  zIndex: 3,
                                  minWidth: 160,
                                  border: '1px solid rgba(73, 32, 153, 0.18)',
                                  borderRadius: 12,
                                  background: '#fff',
                                  boxShadow: '0 10px 20px rgba(73, 32, 153, 0.14)',
                                  overflow: 'hidden'
                                }}
                              >
                                <button
                                  type="button"
                                  onClick={() => handleViewNoteConcepts(note)}
                                  style={{
                                    width: '100%',
                                    border: 'none',
                                    background: 'none',
                                    textAlign: 'left',
                                    padding: '10px 12px',
                                    color: '#4A3B66',
                                    fontSize: 13,
                                    fontWeight: 600,
                                    cursor: 'pointer'
                                  }}
                                >
                                  View note concepts
                                </button>
                                <button
                                  type="button"
                                  onClick={() => deleteNoteById(note.id, false)}
                                  disabled={deletingNoteId === note.id}
                                  style={{
                                    width: '100%',
                                    border: 'none',
                                    borderTop: '1px solid rgba(73, 32, 153, 0.1)',
                                    background: 'none',
                                    textAlign: 'left',
                                    padding: '10px 12px',
                                    color: '#C83737',
                                    fontSize: 13,
                                    fontWeight: 700,
                                    cursor: deletingNoteId === note.id ? 'not-allowed' : 'pointer',
                                    opacity: deletingNoteId === note.id ? 0.6 : 1
                                  }}
                                >
                                  Delete note
                                </button>
                              </div>
                            )}
                            <ChevronRightIcon fontSize="small" style={{ color: '#7c6f96' }} />
                          </div>
                        );
                      }) : (
                        <div className="nr-search-empty">No notes match your search.</div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  {!showSearchWorkspace && !showSearchResultsPage && !showConceptPage && (
                    <h1 className="nr-home-title">Jump back into...</h1>
                  )}
                  {!showConceptPage && (
                    <div className="nr-search">
                      <SearchIcon sx={{ color: 'var(--nr-accent-dark)' }} />
                      <input
                        type="text"
                        placeholder="Search for a person, idea, or topic..."
                        value={mobileSearchValue}
                        onChange={handleSearchInputChange}
                        onKeyDown={(event) => {
                          if (event.key === "Enter") {
                            handleSearchSubmit();
                          }
                        }}
                        aria-label="Search notes"
                      />
                      {searchInputValue && (
                        <button type="button" className="nr-clear-btn" onClick={handleClearSearch}>
                          Clear
                        </button>
                      )}
                    </div>
                  )}
                  {!showSearchWorkspace && !showSearchResultsPage && !showConceptPage && (
                    <div className="nr-explore">
                      <span className="nr-explore-label">Explore</span>
                      <div className="nr-filter-row">
                        <div style={{ display: "flex", width: "100%", alignItems: "center", justifyContent: "space-between" }}>
                          <span className="nr-filter-label">Filter by concept type</span>
                          <button
                            type="button"
                            className="nr-filter-clear"
                            onClick={() => setHomeConceptTypeFilter("All")}
                          >
                            Clear filters
                          </button>
                        </div>
                        <div className="nr-filter-buttons">
                          {[
                            { name: "People", icon: <PersonOutlineIcon sx={{ fontSize: 14 }} /> },
                            { name: "Events", icon: <EventOutlinedIcon sx={{ fontSize: 14 }} /> },
                            { name: "Projects", icon: <WorkOutlineIcon sx={{ fontSize: 14 }} /> },
                          ].map((filter) => (
                            <button
                              key={filter.name}
                              type="button"
                              className={`nr-filter-btn ${homeConceptTypeFilter === filter.name ? "active" : ""}`}
                              onClick={() => {
                                setHomeConceptTypeFilter(filter.name);
                                setHomeMoreMenuOpen(false);
                              }}
                            >
                              {filter.icon}
                              <span>{filter.name}</span>
                            </button>
                          ))}
                          <div className="nr-filter-other-wrap">
                            <button
                              type="button"
                              className="nr-more-link"
                              onClick={() => setHomeMoreMenuOpen(open => !open)}
                            >
                              More
                            </button>
                            {homeMoreMenuOpen && (
                              <div className="nr-sort-dropdown" role="menu" aria-label="More concept filters" style={{ top: 20 }}>
                                {otherConceptTypeOptions.length > 0 ? otherConceptTypeOptions.map(type => (
                                  <button
                                    type="button"
                                    key={type}
                                    className={`nr-menu-option ${homeConceptTypeFilter === type ? "active" : ""}`}
                                    onClick={() => {
                                      setHomeConceptTypeFilter(type);
                                      setHomeMoreMenuOpen(false);
                                    }}
                                  >
                                    {type}
                                  </button>
                                )) : (
                                  <div className="nr-menu-empty">No extra types</div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="nr-explore-cloud">
                        <div className="nr-chip-row">
                          {mobileExploreTags.length > 0 ? mobileExploreTags.map((tag) => (
                            <button
                              key={tag}
                              type="button"
                              className="nr-chip"
                              onClick={() => handleExploreConceptClick(tag)}
                            >
                              {tag}
                            </button>
                          )) : (
                            <div className="nr-search-empty">Concepts from your notes will appear here after refine.</div>
                          )}
                        </div>
                        {mobileExploreTags.length > 12 && <div className="nr-explore-fade" />}
                      </div>
                    </div>
                  )}
                  {showSearchWorkspace && (
                    <div className="nr-search-shell">
                      <div className="nr-section">
                        <span className="nr-section-title">Concepts</span>
                        {mobileSearchResults.concepts.filter(concept => concept.score > 0).slice(0, 3).length > 0 ? (
                          mobileSearchResults.concepts.filter(concept => concept.score > 0).slice(0, 3).map(concept => (
                            <div key={concept.id} className="nr-row" onClick={() => handleConceptSelect(concept)}>
                              <div className="nr-row-icon">{getConceptIcon(concept.type)}</div>
                              <div className="nr-row-main">
                                <div className="nr-row-title">{concept.name}</div>
                                <div className="nr-row-meta">
                                  {concept.type} · connected to {conceptConnections[concept.name] || 0} notes
                                </div>
                              </div>
                              <ChevronRightIcon fontSize="small" style={{ color: '#7c6f96' }} />
                            </div>
                          ))
                        ) : (
                          <div className="nr-search-empty">No close concept matches yet.</div>
                        )}
                      </div>
                      <div className="nr-section">
                        <span className="nr-section-title">Notes</span>
                        {mobileSearchResults.notes.slice(0, 4).map(note => (
                          <div key={note.id} className="nr-row" onClick={() => handleNoteResultClick(note)}>
                            <div className="nr-row-icon">
                              <DescriptionOutlinedIcon fontSize="small" />
                            </div>
                            <div className="nr-row-main">
                              <div className="nr-row-title">{note.title || "Untitled note"}</div>
                              <div className="nr-row-meta">{note.snippet || "No preview available yet."}</div>
                            </div>
                            <ChevronRightIcon fontSize="small" style={{ color: '#7c6f96' }} />
                          </div>
                        ))}
                        {mobileSearchResults.notes.length === 0 && (
                          <div className="nr-search-empty">Notes will appear as you type.</div>
                        )}
                      </div>
                      <div className="nr-section">
                        <span className="nr-section-title">Actions</span>
                        <button type="button" className="nr-action" onClick={handleNewNote}>
                          Create note about "{activeSearchQuery}"
                          <ChevronRightIcon fontSize="small" />
                        </button>
                        <button type="button" className="nr-action" onClick={handleSearchSubmit}>
                          Search in full text
                          <ChevronRightIcon fontSize="small" />
                        </button>
                      </div>
                    </div>
                  )}
                  {showSearchResultsPage && (
                    <div className="nr-search-shell">
                      <div className="nr-section">
                        <span className="nr-section-title">Did you mean...</span>
                        {mobileSearchResults.concepts.slice(0, 4).map(concept => (
                          <div key={concept.id} className="nr-row" onClick={() => handleConceptSelect(concept)}>
                            <div className="nr-row-icon">{getConceptIcon(concept.type)}</div>
                            <div className="nr-row-main">
                              <div className="nr-row-title">{concept.name}</div>
                              <div className="nr-row-meta">
                                {concept.type} · connected to {conceptConnections[concept.name] || 0} notes
                              </div>
                            </div>
                            <ChevronRightIcon fontSize="small" style={{ color: '#7c6f96' }} />
                          </div>
                        ))}
                      </div>
                      <div className="nr-section">
                        <span className="nr-section-title">Notes</span>
                        {mobileSearchResults.notes.slice(0, 6).map(note => (
                          <div key={note.id} className="nr-row" onClick={() => handleNoteResultClick(note)}>
                            <div className="nr-row-icon">
                              <DescriptionOutlinedIcon fontSize="small" />
                            </div>
                            <div className="nr-row-main">
                              <div className="nr-row-title">{note.title || "Untitled note"}</div>
                              <div className="nr-row-meta">{note.snippet || "No preview available yet."}</div>
                            </div>
                            <ChevronRightIcon fontSize="small" style={{ color: '#7c6f96' }} />
                          </div>
                        ))}
                      </div>
                      <div className="nr-section">
                        <button
                          type="button"
                          className="nr-full-graph"
                          onClick={() =>
                            canEnterGraphFromSearch &&
                            navigate('/graph', { state: buildGraphState(recognizedConcepts[0].name, 'Search > Results > Graph') })
                          }
                          disabled={!canEnterGraphFromSearch}
                        >
                          View full graph
                        </button>
                      </div>
                    </div>
                  )}
                  {showConceptPage && (
                    <div className="nr-concept-page">
                      <button
                        type="button"
                        onClick={() => {
                          if (selectedNote?.id && selectedNote.id !== 'new') {
                            setSelectedConcept(null);
                            setMobileEditorOpen(true);
                            if (location.pathname !== '/newnote') {
                              navigate('/newnote');
                            }
                            return;
                          }
                          setSelectedConcept(null);
                          setSearchPage("home");
                          setHomeConceptTypeFilter("All");
                        }}
                        style={{
                          width: 'fit-content',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 8,
                          color: '#492099',
                          fontSize: 13,
                          fontWeight: 700,
                          textDecoration: 'underline',
                          textUnderlineOffset: 3,
                          border: 'none',
                          background: 'none',
                          padding: 0,
                          cursor: 'pointer'
                        }}
                      >
                        <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
                        {selectedNote?.id && selectedNote.id !== 'new' ? 'Back to note' : 'Back to search'}
                      </button>
                      <div className="nr-summary-strip">
                        <div className="nr-summary-title">{selectedConcept.name}</div>
                        <div className="nr-summary-meta">
                          <span>{selectedConcept.type}</span>
                          <span>{conceptConnections[selectedConcept.name] || 0} notes</span>
                          <span>
                            Last mentioned{" "}
                            {conceptLastMention ? conceptLastMention.toLocaleDateString() : "Unknown"}
                          </span>
                        </div>
                      </div>
                      <div className="nr-section">
                        <span className="nr-section-title">Top Notes</span>
                        {conceptNoteMatches.slice(0, 8).map(note => (
                          <div key={note.id} className="nr-row" onClick={() => handleNoteResultClick(note)}>
                            <div className="nr-row-icon">
                              <DescriptionOutlinedIcon fontSize="small" />
                            </div>
                            <div className="nr-row-main">
                              <div className="nr-row-title">{note.title || "Untitled note"}</div>
                              <div className="nr-row-meta">{getNoteSnippet(extractNoteText(note), selectedConcept.name)}</div>
                            </div>
                            <ChevronRightIcon fontSize="small" style={{ color: '#7c6f96' }} />
                          </div>
                        ))}
                        {conceptNoteMatches.length === 0 && (
                          <div className="nr-search-empty">No notes mention this concept yet.</div>
                        )}
                      </div>
                      <div className="nr-section">
                        <span className="nr-section-title">Related Concepts</span>
                        <div className="nr-chip-row scroll">
                          {relatedConcepts.map(concept => (
                            <button key={concept.id} type="button" className="nr-chip" onClick={() => handleConceptSelect(concept)} style={{ whiteSpace: 'nowrap' }}>
                              {concept.name}
                            </button>
                          ))}
                          {relatedConcepts.length === 0 && (
                            <span className="nr-search-empty">No related concepts yet.</span>
                          )}
                        </div>
                      </div>
                      <div className="nr-section">
                        <span className="nr-section-title">Graph Preview</span>
                        <div className="nr-mini-graph">
                          <svg width="100%" height="140" viewBox="0 0 320 140" role="img" aria-label="Graph preview">
                            {(conceptGraphPreview?.edges || []).map((edge, index) => {
                              const fromNode = conceptGraphPreview.nodes.find(node => node.id === edge.from);
                              const toNode = conceptGraphPreview.nodes.find(node => node.id === edge.to);
                              if (!fromNode || !toNode) return null;
                              return (
                                <line
                                  key={`edge-${edge.from}-${edge.to}-${index}`}
                                  x1={fromNode.x}
                                  y1={fromNode.y}
                                  x2={toNode.x}
                                  y2={toNode.y}
                                  stroke={edge.isPrimary ? '#b7aacd' : '#c7bdd8'}
                                  strokeWidth={edge.isPrimary ? 0.9 : 0.7}
                                />
                              );
                            })}
                            {(conceptGraphPreview?.nodes || []).map((node) => (
                              <g key={node.id}>
                                <circle
                                  cx={node.x}
                                  cy={node.y}
                                  r={node.isCenter ? 16 : 11}
                                  fill={node.isCenter ? '#492099' : '#cfc2e7'}
                                />
                                <text
                                  x={node.x}
                                  y={node.y}
                                  textAnchor="middle"
                                  fontSize={node.isCenter ? 8.5 : 7.6}
                                  fontWeight={node.isCenter ? 700 : 600}
                                  fill={node.isCenter ? '#e5e7eb' : '#4f3f70'}
                                >
                                  {wrapPreviewLabel(node.id, node.isCenter ? 10 : 8).map((line, lineIndex, arr) => (
                                    <tspan
                                      key={`${node.id}-${lineIndex}`}
                                      x={node.x}
                                      dy={lineIndex === 0 ? `${-(arr.length - 1) * 0.52}em` : '1.05em'}
                                    >
                                      {line}
                                    </tspan>
                                  ))}
                                </text>
                              </g>
                            ))}
                          </svg>
                          <button
                            type="button"
                            className="nr-full-graph"
                            onClick={() =>
                              navigate('/graph', { state: buildGraphState(selectedConcept.name, 'Search > Concept > Graph') })
                            }
                          >
                            Full graph
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
            <button
              type="button"
              className={`nr-fab ${isSearching ? 'nr-fab--searching' : ''}`}
              onClick={handleNewNote}
              onTouchStart={() => {
                editorRef.current?.focusEditor?.();
              }}
              aria-label="Create new note"
            >
              <AddIcon sx={{ fontSize: 36 }} />
            </button>
            <nav className="nr-bottom-nav" aria-label="Primary">
              <button
                type="button"
                className={`nr-nav-item ${mobilePrimaryView === 'home' ? 'active' : ''}`}
                onClick={handleOpenHome}
              >
                <SearchIcon fontSize="small" />
                <span>Explore</span>
              </button>
              <button
                type="button"
                className={`nr-nav-item ${mobilePrimaryView === 'notes' ? 'active' : ''}`}
                onClick={handleOpenNotesList}
              >
                <DescriptionOutlinedIcon fontSize="small" />
                <span>Notes</span>
              </button>
              <button
                type="button"
                className={`nr-nav-item ${location.pathname === '/profile' ? 'active' : ''}`}
                onClick={() => navigate('/profile')}
              >
                <SettingsIcon fontSize="small" />
                <span>Support</span>
              </button>
            </nav>
          </div>
        </div>

        {/* Mobile: Editor View */}
        <div style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          background: 'linear-gradient(180deg, #f7f4fb 0%, #ffffff 52%, #f2eef9 100%)',
          overflow: 'hidden',
          opacity: showEditorOnMobile ? 1 : 0,
          pointerEvents: showEditorOnMobile ? 'auto' : 'none',
          transition: 'opacity 160ms ease'
        }}>
          <div className="nr-home-bg">
            <div className="nr-orb nr-orb--one" />
            <div className="nr-orb nr-orb--two" />
          </div>
          <div
            className="nr-home-content"
            style={{
              gap: 14,
              paddingBottom: 'calc(env(safe-area-inset-bottom) + 58px)'
            }}
          >
            <div className="nr-brand">
              <div className="nr-brand-main">
                <img
                  src="/logo2-removebg-preview.png"
                  alt="NoteRefinery logo"
                  style={{ width: 42, height: 42 }}
                />
                <span>NoteRefinery</span>
              </div>
              <div className="nr-brand-menu-wrap">
                <button
                  type="button"
                  className="nr-header-menu-btn"
                  onClick={() => setMobileHeaderMenuOpen(open => !open)}
                  aria-label="Open menu"
                  aria-haspopup="menu"
                  aria-expanded={mobileHeaderMenuOpen}
                >
                  <MenuIcon sx={{ fontSize: 18 }} />
                </button>
                {mobileHeaderMenuOpen && (
                  <div className="nr-sort-dropdown" role="menu" aria-label="Account menu" style={{ top: 40, right: 0 }}>
                    <button
                      type="button"
                      className="nr-menu-option"
                      onClick={() => {
                        setMobileHeaderMenuOpen(false);
                        handleLogout();
                      }}
                    >
                      Log out
                    </button>
                  </div>
                )}
              </div>
            </div>
            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                overflowY: 'hidden',
                minHeight: 0
              }}
            >
              {linkedConcepts.length > 0 && (
                <div style={{ padding: '10px 8px 4px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: '#6B4A94', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      Linked concepts
                    </span>
                    <button
                      type="button"
                      onClick={() => setShowLinkedConcepts(value => !value)}
                      style={{ border: 'none', background: 'none', color: '#492099', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >
                      {showLinkedConcepts ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {showLinkedConcepts && (
                    <div style={{ display: 'flex', gap: 8, overflowX: 'auto', paddingBottom: 6 }}>
                      {linkedConcepts.map(concept => (
                        <button
                          key={concept}
                          type="button"
                          onClick={() => {
                            const normalized = concept.toLowerCase().trim();
                            const match = conceptNodes.find(node => (
                              String(node.name || '').toLowerCase().trim() === normalized
                              || String(node.id || '').toLowerCase().trim() === normalized
                            ));
                            handleConceptSelect(match || { id: concept, name: concept, type: 'Topic' });
                          }}
                          style={{
                            borderRadius: 999,
                            border: '1px solid rgba(73, 32, 153, 0.2)',
                            background: 'rgba(73, 32, 153, 0.08)',
                            color: '#492099',
                            padding: '6px 12px',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: 'pointer'
                          }}
                        >
                          {concept}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <NewNote
                selectedNote={selectedNote}
                onNewNote={handleNewNote}
                onNotesChanged={handleNotesChanged}
                setSelectedNote={setSelectedNote}
                editorRef={editorRef}
                readOnly={!showEditorOnMobile}
                onRefineStateChange={handleRefineState}
                highlightQuery={highlightQuery}
                editorLabelAction={mobileEditorLabelAction}
                refinedEntities={selectedNoteRefinedEntities}
                onConceptClick={handleConceptSelect}
                onRefinedStaticViewChange={setShowingRefinedStaticView}
                onSeeEntitiesRelations={() => {
                  if (!selectedNote?.id || selectedNote.id === 'new') return;
                  handleViewNoteConcepts(selectedNote);
                }}
              />
            </div>
          </div>
          {!keyboardVisible && (
            <nav className="nr-bottom-nav" aria-label="Primary">
              <button
                type="button"
                className={`nr-nav-item ${mobilePrimaryView === 'home' ? 'active' : ''}`}
                onClick={handleOpenHome}
              >
                <SearchIcon fontSize="small" />
                <span>Explore</span>
              </button>
              <button
                type="button"
                className={`nr-nav-item ${mobilePrimaryView === 'notes' ? 'active' : ''}`}
                onClick={handleOpenNotesList}
              >
                <DescriptionOutlinedIcon fontSize="small" />
                <span>Notes</span>
              </button>
              <button
                type="button"
                className={`nr-nav-item ${location.pathname === '/profile' ? 'active' : ''}`}
                onClick={() => navigate('/profile')}
              >
                <SettingsIcon fontSize="small" />
                <span>Support</span>
              </button>
            </nav>
          )}
        </div>
        {showFloatingEditorControls && (
          <>
            <button
              className="nr-fab"
              onClick={handleRefine}
              aria-disabled={!canRefine || isRefining}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                background: canRefine
                  ? 'linear-gradient(135deg, #D7C4FA 0%, #8C60E6 45%, #4B1FC5 100%)'
                  : 'linear-gradient(135deg, #F6F1FD 0%, #E8DDF8 55%, #D2C2EE 100%)',
                color: canRefine ? '#FFFFFF' : '#4E465E',
                border: 'none',
                outline: 'none',
                backdropFilter: 'saturate(140%) blur(10px)',
                WebkitBackdropFilter: 'saturate(140%) blur(10px)',
                fontWeight: 600,
                fontSize: '12px',
                cursor: (canRefine && !isRefining) ? 'pointer' : 'not-allowed',
                transition: 'all 120ms ease',
                boxShadow: canRefine ? '0 12px 26px rgba(90, 50, 186, 0.38)' : 'none',
                animation: (canRefine && !isRefining) ? 'refinePulse 2.2s ease-in-out infinite' : 'none',
                willChange: 'box-shadow, filter, opacity',
                zIndex: 20,
                opacity: isRefining ? 0.82 : 1
              }}
              aria-label="Refine note"
            >
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, transform: 'translateY(-2px)' }}>
                <SparklesIcon style={{ width: 22, height: 22 }} />
                <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.02em' }}>{isRefining ? 'Working' : 'Refine'}</span>
              </div>
            </button>
          </>
        )}
        {showFloatingEditorControls && showRefineHint && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: 'fixed',
              left: '50%',
              bottom: 'calc(env(safe-area-inset-bottom) + 110px + 86px)',
              transform: 'translateX(-50%)',
              maxWidth: 220,
              padding: '10px 14px',
              background: '#ffffff',
              color: '#4A3B66',
              fontSize: 12,
              fontWeight: 600,
              borderRadius: 16,
              boxShadow: '0 10px 22px rgba(73, 32, 153, 0.18)',
              pointerEvents: 'none',
              zIndex: 21
            }}
          >
            At least 5 words required to refine.
          </div>
        )}
        {refinePanel}
      </div>
    );
  }

  // Render Desktop Layout
  return (
    <div id="note-view-container-desktop" style={{ display: 'flex', width: '100%', height: '100%', background: 'linear-gradient(180deg, #f7f4fb 0%, #ffffff 56%, #f2eef9 100%)', overflow: 'hidden' }}>
      <style>
        {`
          @keyframes refinePulse {
            0% {
              box-shadow: 0 8px 18px rgba(73, 32, 153, 0.22);
              filter: drop-shadow(0 0 0 rgba(73, 32, 153, 0));
              opacity: 1;
            }
            50% {
              box-shadow: 0 14px 26px rgba(73, 32, 153, 0.42);
              filter: drop-shadow(0 0 6px rgba(73, 32, 153, 0.22));
              opacity: 0.95;
            }
            100% {
              box-shadow: 0 8px 18px rgba(73, 32, 153, 0.22);
              filter: drop-shadow(0 0 0 rgba(73, 32, 153, 0));
              opacity: 1;
            }
          }
          @keyframes nrStreamFlow {
            0% { transform: translateX(-2px) scale(0.8); opacity: 0.35; }
            50% { transform: translateX(8px) scale(1); opacity: 1; }
            100% { transform: translateX(18px) scale(0.7); opacity: 0.3; }
          }
          @keyframes nrCorePulse {
            0% { transform: scale(0.94); box-shadow: 0 0 0 rgba(73, 32, 153, 0.2); }
            50% { transform: scale(1.06); box-shadow: 0 0 0 8px rgba(73, 32, 153, 0.05); }
            100% { transform: scale(0.94); box-shadow: 0 0 0 rgba(73, 32, 153, 0.2); }
          }
          @keyframes nrReveal {
            from { opacity: 0; transform: translateY(8px); }
            to { opacity: 1; transform: translateY(0); }
          }
          #note-view-container-desktop .nr-refine-animation {
            width: 64px;
            height: 48px;
            position: relative;
            border-radius: 14px;
            border: 1px solid rgba(73, 32, 153, 0.16);
            background: linear-gradient(180deg, #f6f1fd 0%, #ffffff 100%);
            overflow: hidden;
            flex-shrink: 0;
          }
          #note-view-container-desktop .nr-refine-core {
            position: absolute;
            right: 6px;
            top: 50%;
            width: 16px;
            height: 16px;
            margin-top: -8px;
            border-radius: 50%;
            background: #492099;
            animation: nrCorePulse 1.3s ease-in-out infinite;
          }
          #note-view-container-desktop .nr-refine-stream {
            position: absolute;
            left: 8px;
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: rgba(73, 32, 153, 0.7);
            animation: nrStreamFlow 1.2s ease-in-out infinite;
          }
          #note-view-container-desktop .nr-refine-stream--one { top: 11px; }
          #note-view-container-desktop .nr-refine-stream--two { top: 20px; animation-delay: 0.16s; }
          #note-view-container-desktop .nr-refine-stream--three { top: 29px; animation-delay: 0.3s; }
          @media (prefers-reduced-motion: reduce) {
            #note-view-container-desktop .nr-refine-stream,
            #note-view-container-desktop .nr-refine-core,
            #note-view-container-desktop [style*="nrReveal"],
            #note-view-container-desktop [style*="refinePulse"] {
              animation: none !important;
            }
          }
        `}
      </style>
      <aside style={{ width: 'min(56vw, 700px)', minWidth: 420, borderRight: '1px solid rgba(73, 32, 153, 0.14)', background: 'rgba(255,255,255,0.72)', backdropFilter: 'blur(6px)', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div style={{ padding: '24px 20px 8px', borderBottom: '1px solid rgba(73, 32, 153, 0.08)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, color: '#492099', fontSize: 24, fontWeight: 700, marginBottom: 32 }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <img src="/logo2-removebg-preview.png" alt="NoteRefinery logo" style={{ width: 28, height: 28 }} />
              <span style={{ fontSize: 24, lineHeight: 1 }}>NoteRefinery</span>
            </div>
            <div className="nr-brand-menu-wrap" style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', zIndex: 90 }}>
              <button
                type="button"
                className="nr-header-menu-btn"
                onClick={() => setMobileHeaderMenuOpen(open => !open)}
                aria-label="Open menu"
                aria-haspopup="menu"
                aria-expanded={mobileHeaderMenuOpen}
                style={{
                  border: '1px solid rgba(73, 32, 153, 0.2)',
                  background: 'rgba(255, 255, 255, 0.78)',
                  color: '#492099',
                  borderRadius: 999,
                  width: 34,
                  height: 34,
                  padding: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <MenuIcon sx={{ fontSize: 18 }} />
              </button>
              {mobileHeaderMenuOpen && (
                <div
                  role="menu"
                  aria-label="Account menu"
                  style={{
                    position: 'absolute',
                    top: 40,
                    right: 0,
                    zIndex: 140,
                    minWidth: 150,
                    border: '1px solid rgba(73, 32, 153, 0.2)',
                    borderRadius: 10,
                    background: '#fff',
                    boxShadow: '0 10px 20px rgba(73, 32, 153, 0.14)',
                    overflow: 'hidden'
                  }}
                >
                  <button
                    type="button"
                    onClick={() => {
                      setMobileHeaderMenuOpen(false);
                      handleLogout();
                    }}
                    style={{
                      width: '100%',
                      border: 'none',
                      background: 'none',
                      textAlign: 'left',
                      padding: '10px 12px',
                      color: '#4b3c67',
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: 'pointer'
                    }}
                  >
                    Log out
                  </button>
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 4 }}>
            <button type="button" onClick={handleOpenHome} style={{ border: 'none', borderBottom: mobilePrimaryView === 'home' ? '2px solid #492099' : '2px solid transparent', borderRadius: 0, background: 'transparent', color: mobilePrimaryView === 'home' ? '#2B2140' : '#6f5a97', fontSize: 15, fontWeight: 700, padding: '8px 12px 7px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <SearchIcon sx={{ fontSize: 16 }} />
              Explore
            </button>
            <button type="button" onClick={handleOpenNotesList} style={{ border: 'none', borderBottom: mobilePrimaryView === 'notes' ? '2px solid #492099' : '2px solid transparent', borderRadius: 0, background: 'transparent', color: mobilePrimaryView === 'notes' ? '#2B2140' : '#6f5a97', fontSize: 15, fontWeight: 700, padding: '8px 12px 7px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 16 }} />
              Notes
            </button>
            <button type="button" onClick={handleOpenSupport} style={{ border: 'none', borderBottom: mobilePrimaryView === 'support' ? '2px solid #492099' : '2px solid transparent', borderRadius: 0, background: 'transparent', color: mobilePrimaryView === 'support' ? '#2B2140' : '#6f5a97', fontSize: 15, fontWeight: 700, padding: '8px 12px 7px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7 }}>
              <SettingsIcon sx={{ fontSize: 16 }} />
              Support
            </button>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '24px 20px 20px' }}>
          {mobilePrimaryView === "notes" ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 8 }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#6f5a97', fontWeight: 700 }}>Your Notes</div>
                <button
                  type="button"
                  onClick={handleNewNote}
                  style={{
                    border: '1px solid rgba(73, 32, 153, 0.22)',
                    borderRadius: 999,
                    background: 'rgba(73, 32, 153, 0.06)',
                    color: '#492099',
                    fontSize: 12,
                    fontWeight: 700,
                    padding: '5px 10px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6
                  }}
                >
                  <AddIcon sx={{ fontSize: 14 }} />
                  New note
                </button>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', borderRadius: 999, padding: '12px 16px', border: '1px solid rgba(73, 32, 153, 0.18)', boxShadow: '0 8px 18px rgba(73, 32, 153, 0.08)' }}>
                <SearchIcon sx={{ color: '#492099' }} />
                <input type="text" placeholder="Search notes by title or summary..." value={notesSearchValue} onChange={(event) => setNotesSearchValue(event.target.value)} aria-label="Search your notes" style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, fontSize: 15, color: '#2b2140' }} />
                {notesSearchValue && <button type="button" onClick={() => setNotesSearchValue("")} style={{ border: 'none', background: 'none', color: '#492099', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Clear</button>}
              </div>
              <div style={{ marginTop: 10, marginBottom: 10, display: 'flex', justifyContent: 'flex-end', position: 'relative' }}>
                <button type="button" onClick={() => setNotesSortMenuOpen(open => !open)} aria-haspopup="menu" aria-expanded={notesSortMenuOpen} style={{ border: 'none', background: 'none', padding: 0, color: '#492099', fontSize: 13, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}>
                  Sort by
                </button>
                {notesSortMenuOpen && (
                  <div role="menu" aria-label="Sort notes" style={{ position: 'absolute', top: 22, right: 0, zIndex: 4, minWidth: 150, border: '1px solid rgba(73, 32, 153, 0.2)', borderRadius: 10, background: '#fff', boxShadow: '0 10px 20px rgba(73, 32, 153, 0.14)' }}>
                    <button type="button" onClick={() => { setNotesSortOrder("newest"); setNotesSortMenuOpen(false); }} style={{ width: '100%', border: 'none', background: notesSortOrder === "newest" ? 'rgba(73, 32, 153, 0.08)' : 'none', textAlign: 'left', padding: '10px 12px', color: notesSortOrder === "newest" ? '#492099' : '#4b3c67', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      Newest first
                    </button>
                    <button type="button" onClick={() => { setNotesSortOrder("oldest"); setNotesSortMenuOpen(false); }} style={{ width: '100%', border: 'none', background: notesSortOrder === "oldest" ? 'rgba(73, 32, 153, 0.08)' : 'none', textAlign: 'left', padding: '10px 12px', color: notesSortOrder === "oldest" ? '#492099' : '#4b3c67', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                      Oldest first
                    </button>
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {filteredSortedNotes.length > 0 ? filteredSortedNotes.map(note => {
                  const summary = getAiSummaryForNote(note) || getNoteSnippet(extractNoteText(note), "") || "No summary yet.";
                  const noteDate = new Date(note.modified_at || note.updated_at || note.created_at || note.date || 0);
                  const dateText = Number.isNaN(noteDate.getTime()) ? "" : noteDate.toLocaleDateString();
                  return (
                    <div key={note.id} style={{ position: 'relative', border: '1px solid rgba(73, 32, 153, 0.14)', borderRadius: 14, background: '#fff', padding: '12px 10px 12px 12px', cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: 10 }} onClick={() => { setNoteRowMenuOpenId(null); handleSelectNote(note); }}>
                      <div style={{ width: 26, height: 26, borderRadius: 8, background: 'rgba(73, 32, 153, 0.08)', color: '#492099', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <DescriptionOutlinedIcon fontSize="small" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, fontWeight: 700, color: '#2b2140' }}>{note.title || "Untitled note"}</div>
                        <div style={{ marginTop: 2, fontSize: 12, lineHeight: 1.35, color: '#6e5e8d' }}>{summary}</div>
                        {dateText && <div style={{ marginTop: 5, fontSize: 11, color: '#8b7fa3' }}>{dateText}</div>}
                      </div>
                      <button type="button" onClick={(event) => { event.stopPropagation(); setNoteRowMenuOpenId(current => (current === note.id ? null : note.id)); }} aria-label="More options" style={{ border: 'none', background: 'none', color: '#6f5a97', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', padding: 0, marginTop: 2 }}>
                        <MoreVertIcon fontSize="small" />
                      </button>
                      {noteRowMenuOpenId === note.id && (
                        <div onClick={(event) => event.stopPropagation()} style={{ position: 'absolute', right: 28, top: 34, zIndex: 3, minWidth: 170, border: '1px solid rgba(73, 32, 153, 0.18)', borderRadius: 12, background: '#fff', boxShadow: '0 10px 20px rgba(73, 32, 153, 0.14)', overflow: 'hidden' }}>
                          <button type="button" onClick={() => handleViewNoteConcepts(note)} style={{ width: '100%', border: 'none', background: 'none', textAlign: 'left', padding: '10px 12px', color: '#4A3B66', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                            View note concepts
                          </button>
                          <button type="button" onClick={() => deleteNoteById(note.id, false)} disabled={deletingNoteId === note.id} style={{ width: '100%', border: 'none', borderTop: '1px solid rgba(73, 32, 153, 0.1)', background: 'none', textAlign: 'left', padding: '10px 12px', color: '#C83737', fontSize: 13, fontWeight: 700, cursor: deletingNoteId === note.id ? 'not-allowed' : 'pointer', opacity: deletingNoteId === note.id ? 0.6 : 1 }}>
                            Delete note
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }) : (
                  <div style={{ color: '#7c6f96', fontSize: 13 }}>No notes match your search.</div>
                )}
              </div>
            </>
          ) : mobilePrimaryView === "support" ? (
            <div style={{ borderRadius: 14, border: '1px solid rgba(73, 32, 153, 0.14)', background: 'rgba(255,255,255,0.88)', boxShadow: '0 12px 22px rgba(73, 32, 153, 0.08)', padding: 14 }}>
              <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.18em', color: '#7c6f96', fontWeight: 700, marginBottom: 8 }}>Support</div>
              <div style={{ color: '#5e4e7a', fontSize: 14, marginBottom: 14 }}>Account help and product support.</div>
              <div style={{ color: '#2d2342', fontSize: 14, fontWeight: 700, marginBottom: 8 }}>Your account usage</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 10, marginBottom: 12 }}>
                <div style={{ borderRadius: 10, border: '1px solid rgba(73, 32, 153, 0.1)', background: '#fff', padding: 12, textAlign: 'center' }}>
                  <div style={{ color: '#6e5e8d', fontSize: 11, marginBottom: 4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Notes</div>
                  <div style={{ color: '#2d2342', fontSize: 24, fontWeight: 800 }}>{mobileNotes.length}</div>
                </div>
                <div style={{ borderRadius: 10, border: '1px solid rgba(73, 32, 153, 0.1)', background: '#fff', padding: 12, textAlign: 'center' }}>
                  <div style={{ color: '#6e5e8d', fontSize: 11, marginBottom: 4, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Concepts</div>
                  <div style={{ color: '#2d2342', fontSize: 24, fontWeight: 800 }}>{conceptNodes.length}</div>
                </div>
              </div>
              <div style={{ borderRadius: 10, border: '1px solid rgba(73, 32, 153, 0.1)', background: '#fff', padding: 12 }}>
                <div style={{ color: '#2d2342', fontSize: 14, fontWeight: 700, marginBottom: 6 }}>Need help with your account?</div>
                <div style={{ color: '#5e4e7a', fontSize: 13.5, marginBottom: 10 }}>Send us a quick message and we will help with access, notes, or graph issues.</div>
                <a href="mailto:maria@littleoxfordstreet.com" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none', borderRadius: 999, padding: '7px 12px', color: '#492099', border: '1px solid rgba(73, 32, 153, 0.25)', background: 'rgba(73, 32, 153, 0.04)', fontWeight: 700, fontSize: 13 }}>
                  maria@littleoxfordstreet.com
                </a>
              </div>
            </div>
          ) : (
            <>
              {!showSearchWorkspace && !showSearchResultsPage && !showConceptPage && (
                <h1 style={{ fontSize: 34, fontWeight: 650, lineHeight: 1.15, color: '#2b2140', margin: '4px 0 12px' }}>Jump back into...</h1>
              )}
              {!showConceptPage && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff', borderRadius: 999, padding: '12px 16px', border: '1px solid rgba(73, 32, 153, 0.18)', boxShadow: '0 10px 25px rgba(73, 32, 153, 0.08)' }}>
                  <SearchIcon sx={{ color: '#492099' }} />
                  <input type="text" placeholder="Search for a person, idea, or topic..." value={mobileSearchValue} onChange={handleSearchInputChange} onKeyDown={(event) => { if (event.key === "Enter") { handleSearchSubmit(); } }} aria-label="Search notes" style={{ border: 'none', outline: 'none', background: 'transparent', flex: 1, fontSize: 15, color: '#2b2140' }} />
                  {searchInputValue && <button type="button" onClick={handleClearSearch} style={{ border: 'none', background: 'none', color: '#492099', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Clear</button>}
                </div>
              )}
              {!showSearchWorkspace && !showSearchResultsPage && !showConceptPage && (
                <div style={{ marginTop: 16 }}>
                  <div style={{ textTransform: 'uppercase', fontSize: 12, letterSpacing: '0.18em', color: '#5a4a7b', fontWeight: 700 }}>Explore</div>
                  <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 12, color: '#7c6f96', fontWeight: 600 }}>Filter by concept type</span>
                    <button type="button" onClick={() => setHomeConceptTypeFilter("All")} style={{ border: 'none', background: 'none', color: '#6f5a97', fontSize: 12, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}>Clear filters</button>
                  </div>
                  <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    {[{ name: "People", icon: <PersonOutlineIcon sx={{ fontSize: 14 }} /> }, { name: "Events", icon: <EventOutlinedIcon sx={{ fontSize: 14 }} /> }, { name: "Projects", icon: <WorkOutlineIcon sx={{ fontSize: 14 }} /> }].map((filter) => (
                      <button key={filter.name} type="button" onClick={() => { setHomeConceptTypeFilter(filter.name); setHomeMoreMenuOpen(false); }} style={{ border: '1px dotted rgba(73, 32, 153, 0.55)', borderRadius: 999, background: 'transparent', color: '#492099', fontSize: 12, fontWeight: 700, padding: '5px 12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                        {filter.icon}
                        <span>{filter.name}</span>
                      </button>
                    ))}
                    <div style={{ marginLeft: 'auto', position: 'relative' }}>
                      <button type="button" onClick={() => setHomeMoreMenuOpen(open => !open)} style={{ border: 'none', background: 'none', color: '#6f5a97', fontSize: 12, fontWeight: 700, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}>More</button>
                      {homeMoreMenuOpen && (
                        <div role="menu" aria-label="More concept filters" style={{ position: 'absolute', top: 20, right: 0, zIndex: 3, minWidth: 160, border: '1px solid rgba(73, 32, 153, 0.2)', borderRadius: 10, background: '#fff', boxShadow: '0 10px 20px rgba(73, 32, 153, 0.14)' }}>
                          {otherConceptTypeOptions.length > 0 ? otherConceptTypeOptions.map(type => (
                            <button key={type} type="button" onClick={() => { setHomeConceptTypeFilter(type); setHomeMoreMenuOpen(false); }} style={{ width: '100%', border: 'none', background: homeConceptTypeFilter === type ? 'rgba(73, 32, 153, 0.08)' : 'none', textAlign: 'left', padding: '10px 12px', color: homeConceptTypeFilter === type ? '#492099' : '#4b3c67', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                              {type}
                            </button>
                          )) : <div style={{ padding: '10px 12px', color: '#8b7fa3', fontSize: 12 }}>No extra types</div>}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ marginTop: 12, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                    {mobileExploreTags.length > 0 ? mobileExploreTags.map((tag) => (
                      <button key={tag} type="button" onClick={() => handleExploreConceptClick(tag)} style={{ borderRadius: 999, padding: '8px 14px', border: '1px solid rgba(73, 32, 153, 0.18)', background: 'rgba(92, 59, 139, 0.08)', color: '#492099', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
                        {tag}
                      </button>
                    )) : <div style={{ color: '#7c6f96', fontSize: 13 }}>Concepts from your notes will appear here after refine.</div>}
                  </div>
                </div>
              )}
              {showSearchWorkspace && (
                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ border: '1px solid rgba(73, 32, 153, 0.12)', borderRadius: 14, background: 'rgba(255,255,255,0.82)', padding: 12 }}>
                    <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#6f5a97', fontWeight: 700, marginBottom: 8 }}>Concepts</span>
                    {mobileSearchResults.concepts.filter(concept => concept.score > 0).slice(0, 3).length > 0 ? (
                      mobileSearchResults.concepts.filter(concept => concept.score > 0).slice(0, 3).map(concept => (
                        <div key={concept.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', cursor: 'pointer' }} onClick={() => handleConceptSelect(concept)}>
                          <div style={{ color: '#492099', display: 'inline-flex' }}>{getConceptIcon(concept.type)}</div>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 14, color: '#2b2140', fontWeight: 700 }}>{concept.name}</div>
                            <div style={{ fontSize: 12, color: '#7c6f96' }}>{concept.type} · connected to {conceptConnections[concept.name] || 0} notes</div>
                          </div>
                          <ChevronRightIcon fontSize="small" style={{ color: '#7c6f96' }} />
                        </div>
                      ))
                    ) : <div style={{ color: '#7c6f96', fontSize: 13 }}>No close concept matches yet.</div>}
                  </div>
                  <div style={{ border: '1px solid rgba(73, 32, 153, 0.12)', borderRadius: 14, background: 'rgba(255,255,255,0.82)', padding: 12 }}>
                    <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#6f5a97', fontWeight: 700, marginBottom: 8 }}>Notes</span>
                    {mobileSearchResults.notes.slice(0, 4).map(note => (
                      <div key={note.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', cursor: 'pointer' }} onClick={() => handleNoteResultClick(note)}>
                        <div style={{ width: 24, height: 24, borderRadius: 8, background: 'rgba(73, 32, 153, 0.08)', color: '#492099', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <DescriptionOutlinedIcon fontSize="small" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, color: '#2b2140', fontWeight: 700 }}>{note.title || "Untitled note"}</div>
                          <div style={{ fontSize: 12, color: '#7c6f96' }}>{note.snippet || "No preview available yet."}</div>
                        </div>
                        <ChevronRightIcon fontSize="small" style={{ color: '#7c6f96' }} />
                      </div>
                    ))}
                    {mobileSearchResults.notes.length === 0 && <div style={{ color: '#7c6f96', fontSize: 13 }}>Notes will appear as you type.</div>}
                  </div>
                </div>
              )}
              {showSearchResultsPage && (
                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ border: '1px solid rgba(73, 32, 153, 0.12)', borderRadius: 14, background: 'rgba(255,255,255,0.82)', padding: 12 }}>
                    <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#6f5a97', fontWeight: 700, marginBottom: 8 }}>Did you mean...</span>
                    {mobileSearchResults.concepts.slice(0, 4).map(concept => (
                      <div key={concept.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', cursor: 'pointer' }} onClick={() => handleConceptSelect(concept)}>
                        <div style={{ color: '#492099', display: 'inline-flex' }}>{getConceptIcon(concept.type)}</div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, color: '#2b2140', fontWeight: 700 }}>{concept.name}</div>
                          <div style={{ fontSize: 12, color: '#7c6f96' }}>{concept.type} · connected to {conceptConnections[concept.name] || 0} notes</div>
                        </div>
                        <ChevronRightIcon fontSize="small" style={{ color: '#7c6f96' }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ border: '1px solid rgba(73, 32, 153, 0.12)', borderRadius: 14, background: 'rgba(255,255,255,0.82)', padding: 12 }}>
                    <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#6f5a97', fontWeight: 700, marginBottom: 8 }}>Notes</span>
                    {mobileSearchResults.notes.slice(0, 6).map(note => (
                      <div key={note.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', cursor: 'pointer' }} onClick={() => handleNoteResultClick(note)}>
                        <div style={{ width: 24, height: 24, borderRadius: 8, background: 'rgba(73, 32, 153, 0.08)', color: '#492099', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <DescriptionOutlinedIcon fontSize="small" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, color: '#2b2140', fontWeight: 700 }}>{note.title || "Untitled note"}</div>
                          <div style={{ fontSize: 12, color: '#7c6f96' }}>{note.snippet || "No preview available yet."}</div>
                        </div>
                        <ChevronRightIcon fontSize="small" style={{ color: '#7c6f96' }} />
                      </div>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!canEnterGraphFromSearch) return;
                      if (isMobile) {
                        navigate('/graph', { state: buildGraphState(recognizedConcepts[0].name, 'Search > Results > Graph') });
                        return;
                      }
                      openDesktopFullGraph(recognizedConcepts[0].name);
                    }}
                    disabled={!canEnterGraphFromSearch}
                    style={{ border: 'none', borderRadius: 12, background: canEnterGraphFromSearch ? '#492099' : '#cdc2e4', color: '#fff', padding: '11px 14px', fontSize: 14, fontWeight: 700, cursor: canEnterGraphFromSearch ? 'pointer' : 'not-allowed' }}
                  >
                    View full graph
                  </button>
                </div>
              )}
              {showConceptPage && (
                <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedConcept(null);
                      setSearchPage("home");
                      setHomeConceptTypeFilter("All");
                    }}
                    style={{
                      width: 'fit-content',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      color: '#492099',
                      fontSize: 13,
                      fontWeight: 700,
                      textDecoration: 'underline',
                      textUnderlineOffset: 3,
                      border: 'none',
                      background: 'none',
                      padding: 0,
                      cursor: 'pointer'
                    }}
                  >
                    <ArrowBackIosNewIcon sx={{ fontSize: 14 }} />
                    Back to search
                  </button>
                  <div style={{ borderRadius: 14, border: '1px solid rgba(73, 32, 153, 0.16)', background: '#fff', padding: 12 }}>
                    <div style={{ fontSize: 20, color: '#2b2140', fontWeight: 700 }}>{selectedConcept.name}</div>
                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 10, fontSize: 12, color: '#6f5a97' }}>
                      <span>{selectedConcept.type}</span>
                      <span>{conceptConnections[selectedConcept.name] || 0} notes</span>
                      <span>Last mentioned {conceptLastMention ? conceptLastMention.toLocaleDateString() : "Unknown"}</span>
                    </div>
                  </div>
                  <div style={{ borderRadius: 14, border: '1px solid rgba(73, 32, 153, 0.12)', background: 'rgba(255,255,255,0.84)', padding: 12 }}>
                    <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#6f5a97', fontWeight: 700, marginBottom: 8 }}>Top Notes</span>
                    {conceptNoteMatches.slice(0, 8).map(note => (
                      <div key={note.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', cursor: 'pointer' }} onClick={() => handleNoteResultClick(note)}>
                        <div style={{ width: 24, height: 24, borderRadius: 8, background: 'rgba(73, 32, 153, 0.08)', color: '#492099', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                          <DescriptionOutlinedIcon fontSize="small" />
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, color: '#2b2140', fontWeight: 700 }}>{note.title || "Untitled note"}</div>
                          <div style={{ fontSize: 12, color: '#7c6f96' }}>{getNoteSnippet(extractNoteText(note), selectedConcept.name)}</div>
                        </div>
                        <ChevronRightIcon fontSize="small" style={{ color: '#7c6f96' }} />
                      </div>
                    ))}
                    {conceptNoteMatches.length === 0 && <div style={{ color: '#7c6f96', fontSize: 13 }}>No notes mention this concept yet.</div>}
                  </div>
                  <div style={{ borderRadius: 14, border: '1px solid rgba(73, 32, 153, 0.12)', background: 'rgba(255,255,255,0.84)', padding: 12 }}>
                    <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#6f5a97', fontWeight: 700, marginBottom: 8 }}>Related Concepts</span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {relatedConcepts.map(concept => (
                        <button key={concept.id} type="button" onClick={() => handleConceptSelect(concept)} style={{ borderRadius: 999, border: '1px solid rgba(73, 32, 153, 0.2)', background: 'rgba(73, 32, 153, 0.08)', color: '#492099', padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                          {concept.name}
                        </button>
                      ))}
                      {relatedConcepts.length === 0 && <span style={{ color: '#7c6f96', fontSize: 13 }}>No related concepts yet.</span>}
                    </div>
                  </div>
                  <div style={{ borderRadius: 14, border: '1px solid rgba(73, 32, 153, 0.12)', background: 'rgba(255,255,255,0.84)', padding: 12 }}>
                    <span style={{ display: 'block', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.16em', color: '#6f5a97', fontWeight: 700, marginBottom: 8 }}>Graph Preview</span>
                    <div style={{ borderRadius: 12, border: '1px solid rgba(73, 32, 153, 0.08)', background: '#fff', padding: 10 }}>
                      {conceptGraphPreview ? (
                        <svg width="100%" height="170" viewBox="0 0 320 140" role="img" aria-label="Graph preview">
                          {(conceptGraphPreview.edges || []).map((edge, index) => {
                            const fromNode = conceptGraphPreview.nodes.find(node => node.id === edge.from);
                            const toNode = conceptGraphPreview.nodes.find(node => node.id === edge.to);
                            if (!fromNode || !toNode) return null;
                            return (
                              <line
                                key={`desktop-edge-${edge.from}-${edge.to}-${index}`}
                                x1={fromNode.x}
                                y1={fromNode.y}
                                x2={toNode.x}
                                y2={toNode.y}
                                stroke={edge.isPrimary ? '#b7aacd' : '#c7bdd8'}
                                strokeWidth={edge.isPrimary ? 0.95 : 0.72}
                              />
                            );
                          })}
                          {(conceptGraphPreview.nodes || []).map((node) => (
                            <g key={`desktop-node-${node.id}`}>
                              <circle
                                cx={node.x}
                                cy={node.y}
                                r={node.isCenter ? 16 : 11}
                                fill={node.isCenter ? '#492099' : '#cfc2e7'}
                              />
                              <text
                                x={node.x}
                                y={node.y}
                                textAnchor="middle"
                                fontSize={node.isCenter ? 8.5 : 7.6}
                                fontWeight={node.isCenter ? 700 : 600}
                                fill={node.isCenter ? '#e5e7eb' : '#4f3f70'}
                              >
                                {wrapPreviewLabel(node.id, node.isCenter ? 10 : 8).map((line, lineIndex, arr) => (
                                  <tspan
                                    key={`desktop-label-${node.id}-${lineIndex}`}
                                    x={node.x}
                                    dy={lineIndex === 0 ? `${-(arr.length - 1) * 0.52}em` : '1.05em'}
                                  >
                                    {line}
                                  </tspan>
                                ))}
                              </text>
                            </g>
                          ))}
                        </svg>
                      ) : (
                        <div style={{ color: '#7c6f96', fontSize: 13 }}>No preview data available yet.</div>
                      )}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      if (!selectedConcept?.name) return;
                      if (isMobile) {
                        navigate('/graph', { state: buildGraphState(selectedConcept.name, 'Search > Concept > Graph') });
                        return;
                      }
                      openDesktopFullGraph(selectedConcept.name);
                    }}
                    disabled={!selectedConcept?.name}
                    style={{ border: 'none', borderRadius: 12, background: selectedConcept?.name ? '#492099' : '#cdc2e4', color: '#fff', padding: '11px 14px', fontSize: 14, fontWeight: 700, cursor: selectedConcept?.name ? 'pointer' : 'not-allowed' }}
                  >
                    Full graph
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </aside>

      <section style={{ flex: 1, minWidth: 0, maxWidth: 760, display: 'flex', flexDirection: 'column', minHeight: 0, position: 'relative' }}>
        <div style={{ margin: 10, minHeight: 0, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {showDesktopGraphInPane ? (
            <GraphViewVis
              embedded
              initialCenterConcept={desktopGraphView?.centerConcept || ""}
              initialOriginConcept={desktopGraphView?.originConcept || desktopGraphView?.centerConcept || ""}
              onBack={closeDesktopFullGraph}
              onOpenSearch={handleDesktopGraphOpenSearch}
              onOpenConcept={handleDesktopGraphOpenConcept}
            />
          ) : (
            <>
              {linkedConcepts.length > 0 && (
                <div style={{ padding: '10px 12px 6px', borderBottom: '1px solid rgba(73, 32, 153, 0.1)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: showLinkedConcepts ? 8 : 0 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#6B4A94', letterSpacing: '0.08em', textTransform: 'uppercase' }}>Linked concepts</span>
                    <button type="button" onClick={() => setShowLinkedConcepts(value => !value)} style={{ border: 'none', background: 'none', color: '#492099', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
                      {showLinkedConcepts ? 'Hide' : 'Show'}
                    </button>
                  </div>
                  {showLinkedConcepts && (
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {linkedConcepts.map(concept => (
                        <button
                          key={concept}
                          type="button"
                          onClick={() => {
                            const normalized = concept.toLowerCase().trim();
                            const match = conceptNodes.find(node => (
                              String(node.name || '').toLowerCase().trim() === normalized
                              || String(node.id || '').toLowerCase().trim() === normalized
                            ));
                            handleConceptSelect(match || { id: concept, name: concept, type: 'Topic' });
                          }}
                          style={{ borderRadius: 999, border: '1px solid rgba(73, 32, 153, 0.2)', background: 'rgba(73, 32, 153, 0.08)', color: '#492099', padding: '6px 12px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                        >
                          {concept}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <NewNote selectedNote={selectedNote} onNewNote={handleNewNote} onNotesChanged={handleNotesChanged} setSelectedNote={setSelectedNote} editorRef={editorRef} readOnly={false} onRefineStateChange={handleRefineState} highlightQuery={highlightQuery} framePaddingOffset={{ top: 0, right: 8, bottom: 8, left: 8 }} refinedEntities={selectedNoteRefinedEntities} onConceptClick={handleConceptSelect} onRefinedStaticViewChange={setShowingRefinedStaticView} onSeeEntitiesRelations={() => { if (!selectedNote?.id || selectedNote.id === 'new') return; handleViewNoteConcepts(selectedNote); }} />
            </>
          )}
        </div>
      </section>
      {showFloatingEditorControls && (
        <>
          <div ref={editorMenuRef} style={{ position: 'fixed', right: 28, bottom: 102, zIndex: 22 }}>
            <IconButton
              onClick={() => setEditorMenuOpen(open => !open)}
              size="small"
              sx={{
                color: '#492099',
                padding: '6px',
                border: '1px solid rgba(73, 32, 153, 0.22)',
                backgroundColor: 'rgba(255, 255, 255, 0.96)',
                boxShadow: '0 8px 18px rgba(73, 32, 153, 0.2)',
                '&:hover': { backgroundColor: 'rgba(248, 244, 255, 0.98)' }
              }}
            >
              <MoreVertIcon fontSize="small" />
            </IconButton>
            {editorMenuOpen && (
              <div
                style={{
                  position: 'absolute',
                  right: 0,
                  bottom: 'calc(100% + 8px)',
                  minWidth: 206,
                  borderRadius: 12,
                  border: '1px solid rgba(73, 32, 153, 0.12)',
                  background: 'rgba(255, 255, 255, 0.97)',
                  boxShadow: '0 16px 34px rgba(73, 32, 153, 0.2)',
                  overflow: 'hidden',
                  zIndex: 60
                }}
              >
                <button
                  type="button"
                  onClick={() => {
                    if (isSavingDraft || isRefining) return;
                    setEditorMenuOpen(false);
                    handleSaveDraft();
                  }}
                  disabled={isSavingDraft || isRefining}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    border: 'none',
                    background: 'transparent',
                    color: (isSavingDraft || isRefining) ? '#AEA4C4' : '#2B2140',
                    cursor: (isSavingDraft || isRefining) ? 'not-allowed' : 'pointer',
                    fontSize: 13,
                    fontWeight: 600
                  }}
                >
                  {isSavingDraft ? 'Saving draft...' : 'Save draft'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!canViewSelectedNoteConcepts || !selectedNote?.id) return;
                    setEditorMenuOpen(false);
                    handleViewNoteConcepts(selectedNote);
                  }}
                  disabled={!canViewSelectedNoteConcepts}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    border: 'none',
                    borderTop: '1px solid rgba(73, 32, 153, 0.1)',
                    background: 'transparent',
                    color: canViewSelectedNoteConcepts ? '#2B2140' : '#AEA4C4',
                    cursor: canViewSelectedNoteConcepts ? 'pointer' : 'not-allowed',
                    fontSize: 13,
                    fontWeight: 600
                  }}
                >
                  See entities / relations
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (!canDeleteSelectedNote) return;
                    setEditorMenuOpen(false);
                    deleteNoteById(selectedNote.id, true);
                  }}
                  disabled={!canDeleteSelectedNote || deletingNoteId === selectedNote?.id}
                  style={{
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 12px',
                    border: 'none',
                    borderTop: '1px solid rgba(73, 32, 153, 0.1)',
                    background: 'transparent',
                    color: (canDeleteSelectedNote && deletingNoteId !== selectedNote?.id) ? '#C83737' : '#E1B4B4',
                    cursor: (canDeleteSelectedNote && deletingNoteId !== selectedNote?.id) ? 'pointer' : 'not-allowed',
                    fontSize: 13,
                    fontWeight: 700
                  }}
                >
                  Delete note
                </button>
              </div>
            )}
          </div>
          <button
            onClick={handleRefine}
            aria-disabled={!canRefine || isRefining}
            style={{
              position: 'fixed',
              right: 28,
              bottom: 28,
              width: 72,
              height: 72,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              background: canRefine
                ? 'linear-gradient(135deg, #D7C4FA 0%, #8C60E6 45%, #4B1FC5 100%)'
                : 'linear-gradient(135deg, #F6F1FD 0%, #E8DDF8 55%, #D2C2EE 100%)',
              color: canRefine ? '#FFFFFF' : '#4E465E',
              border: 'none',
              outline: 'none',
              backdropFilter: 'saturate(140%) blur(10px)',
              WebkitBackdropFilter: 'saturate(140%) blur(10px)',
              fontWeight: 600,
              fontSize: '12px',
              cursor: (canRefine && !isRefining) ? 'pointer' : 'not-allowed',
              transition: 'all 120ms ease',
              boxShadow: canRefine ? '0 12px 26px rgba(90, 50, 186, 0.38)' : 'none',
              animation: (canRefine && !isRefining) ? 'refinePulse 2.2s ease-in-out infinite' : 'none',
              willChange: 'box-shadow, filter, opacity',
              zIndex: 20,
              opacity: isRefining ? 0.82 : 1
            }}
            aria-label="Refine note"
          >
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, transform: 'translateY(-2px)' }}>
              <SparklesIcon style={{ width: 22, height: 22 }} />
              <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.02em' }}>{isRefining ? 'Working' : 'Refine'}</span>
            </div>
          </button>
        </>
      )}
      {showFloatingEditorControls && showRefineHint && (
        <div
          role="status"
          aria-live="polite"
          style={{
            position: 'fixed',
            right: 20,
            bottom: 114,
            maxWidth: 220,
            padding: '10px 14px',
            background: '#ffffff',
            color: '#4A3B66',
            fontSize: 12,
            fontWeight: 600,
            borderRadius: 16,
            boxShadow: '0 10px 22px rgba(73, 32, 153, 0.18)',
            pointerEvents: 'none',
            zIndex: 21
          }}
        >
          At least 5 words required to refine.
        </div>
      )}
      {refinePanel}
    </div>
  );
};

export default NoteView;
